<template>
  <div class="page-table-layout" data-testid="tasklist">
      <v-card
        class="all-width flex-card shadow-off"
        loader-height="2"
        :loading="loading || false"
        :disabled="loading || false"
        data-testid="tasklist__main-card"
      >
        <v-card-text class="page-table-layout__body gap-2" data-testid="tasklist__main-card-content">
          <TasklistPageHeader
            ref="tasklistPageHeader"
            :breadcrumbs="breadcrumbs"
            :metadata="metadata"
            :show-noa="chatbotEnabled"
            :activity-log-type="$options.ACTIVITY_LOG_TYPES.TASKLIST"
            :can-import="userType === 'manager' && ($nova.hasRight(right, 'edit') || $nova.hasRight(right, 'add'))"
            :can-add-task="userType === 'manager' && ($nova.hasRight(right, 'edit') || $nova.hasRight(right, 'add'))"
            :expected-headers="this.$constants.EXPECTED_HEADERS_CLOSE_CYCLE"
            :custom-columns="customColumns"
            :chart-of-accounts="chartAcc"
            :project-id="id"
            :active-users="userDetails.activeUsers"
            :location-list="locationList"
            :category-acc="categoryAcc"
            :subcategory-acc="subcategoryAcc"
            :coa-category-acc="coaCategoryAcc"
            :coa-subcategory-acc="coaSubcategoryAcc"
            :user-details="userDetails"
            :export-disabled="!tasklistFullyLoaded"
            :load-progress="tasklistHeaderLoadProgress"
            @refresh="refreshTasklistFromPage()"
            @retry-load="retryTasklistLoad"
            @noa="redirectChatBot()"
            @export="downloadExcel()"
            @reload="refreshData()"
            @status-report="onStatusReportFromHeader"
          />
          <TasksViewToolbarShell
            v-if="hasTasklistMetadata"
            :user-type="userType"
            :show-manager-role="hasManagerRole"
            :view-mode="viewMode"
            :horizon-view-enabled="horizonViewEnabled"
            :loading="loading"
            @role-change="filterListByUser"
            @view-mode-change="viewMode = $event"
          >
            <TasklistToolbar
              v-if="!isHorizonView"
              :loading="loading"
              :metadata="metadata"
              :selected-tab-stage="selectedTabStage"
              :active-summary="activeSummary"
              :has-active-table-filter="hasActiveTableFilter"
              :column-order-changed="columnOrderChanged"
              :selected-tasks.sync="selectedTasks"
              :user-type="userType"
              :over-due-date="overDueDate"
              :table-headers-filter="tableHeadersfilter"
              :page-name="page_name"
              :show-conversation="$nova.hasRight(right, 'edit') || $nova.hasRight(right, 'add')"
              @select-stage="filterWithTask($event)"
              @clear-filter="clearTableFilter()"
              @save-column-order="saveColumnOrder()"
              @overdue-filter="overDueDateFun()"
              @mark-conversation-read="metadata.conversation_read = true"
              @bulk-action="onBulkActionUserFromToolbar($event)"
              @bulk-update-duedate="onBulkDueDateFromToolbar()"
              @bulk-edit-threshold="$refs.tasklistView?.openBulkThresholdDialog()"
              @bulk-edit-tasks="onBulkEditFromToolbar()"
              @bulk-unlink-bot="bulkunlinkBot(selectedTasks)"
              @bulk-download="downloadbbulk(selectedTasks)"
              @bulk-change-status="changeChecklistStatus(selectedTasks, $event)"
              @apply-columns="handleApplyColumns($event)"
              @save-columns="handleSaveColumns($event)"
            />
            <HorizonToolbar
              v-else
              v-bind="horizonToolbarProps"
              :over-due-date="overDueDate"
              @filter-change="onHorizonToolbarFilterChange"
              @date-range-change="onHorizonToolbarDateRangeChange"
              @overdue-filter="overDueDateFun()"
            />
          </TasksViewToolbarShell>
          <div
            v-if="isHorizonView && hasTasklistMetadata"
            class="flex-fill-table"
            data-testid="tasklist__horizon-panel"
          >
            <HorizonLanding
              ref="horizonLanding"
              :loading="loading"
              :over-due-date="overDueDate"
              data-testid="tasklist__horizon-landing"
              @hook:mounted="onHorizonLandingMounted"
              @role-change="onHorizonRoleChange"
              @due-date-updated="onHorizonDueDateUpdated"
              @toolbar-state-change="onHorizonToolbarStateChange"
            />
          </div>
          <div v-else-if="loading && !hasTasklistMetadata" data-testid="tasklist__loading-skeleton">
            <v-skeleton-loader
              type="table-heading, list-item-two-line, article, article, article"
            ></v-skeleton-loader>
          </div>
          <TasklistView
            v-else-if="!isHorizonView"
            ref="tasklistView"
            class="flex-fill-table"
            :loading="loading"
            :metadata="metadata"
            :project-id="id"
            :userid="userid"
            :user-type="userType"
            :right="right"
            :table-headersfilter="tableHeadersfilter"
            :selected-tab-stage="selectedTabStage"
            :over-due-date="overDueDate"
            :search-query="searchQuery"
            :custom-columns="customColumns"
            :task-list-data="taskListData"
            :location-list="locationList"
            :category-acc="categoryAcc"
            :user-map="userMap"
            :users-list="usersList"
            :page_details="page_details"
            :search="search"
            :selected-tasks.sync="selectedTasks"
            :due-date-min-date="dueDateMinDate"
            :due-date-max-date="dueDateMaxDate"
            :user-details="userDetails"
            :transaction-matching-list="transactionMatchingList"
            :bot-list="botlist"
            :lease-schedule-items="leaseScheduleItems"
            :drawer-bindings="tasklistDrawerBindings"
            :show-conversation="$nova.hasRight(right, 'edit') || $nova.hasRight(right, 'add')"
            :bulk-locked="!tasklistFullyLoaded"
            :snap-unloaded-page-to-first="snapUnloadedTasklistPageToFirst"
            @filter-active-change="hasActiveTableFilter = $event"
            @refresh="getRefresh"
            @page-loading-change="loading = $event"
            @column-reorder="onColumnReorder"
            @navigate-task="viewTaskDetails"
            @open-bulk-upload-box="OpenBulkUploadBox"
            @delete-checklist-item="deleteChecklistItem"
            @workpaper-template="workpaperTemplate"
            @download-single-task-files="downloadSingleTaskFiles"
            @change-checklist-status="onRowChangeChecklistStatus"
            @unlink-bot="unlinkBot"
            @view-task-details="viewTaskDetails"
            @edit-task="onEditTaskFromRow"
            @clear-table-filter="onTasklistTableFilterClear"
            @list-view-state-change="onListViewStateChange"
            @bulk-manage-user="onBulkManageUserFromDrawer"
            @bulk-edit-open="clearfileBulk()"
            @checklist-file-change="updateFilesBulk"
            @checklist-reupload="clearfile()"
            @checklist-download-template="downloadTemplate()"
            @checklist-upload="uploadData()"
            @checklist-cancel="cleardata()"
            @update:checklistShowDataWithError="showdatawitherror = $event"
            @bulk-edit-file-change="readBulkEditFile"
            @bulk-edit-reupload="clearfileBulk()"
            @bulk-edit-download-template="downloadExcelTasklist(selectedTasks)"
            @bulk-edit-upload="uploadDataBulk()"
            @bulk-edit-cancel="cleardataBulkEdit()"
            @update:bulkEditShowDataWithError="showdatawitherrorEditBulk = $event"
            @workpaper-files-change="FilesUpload"
            @update:workpaperFileRemark="fileremark = $event"
            @update:workpaperUploadWithoutFile="uploadiwithoutfile = $event"
            @workpaper-download-template="workpaperTemplate()"
            @workpaper-download-file="$nova.downloadFile($event)"
            @workpaper-submit-without-file="sendForReviewBulk()"
            @workpaper-submit-with-files="uploadFilesBulk()"
            @bulk-download="downloadbbulk(selectedTasks)"
            @bulk-change-status="changeChecklistStatus(selectedTasks, $event)"
            @bulk-unlink-bot="bulkunlinkBot(selectedTasks)"
            @mark-conversation-read="metadata.conversation_read = true"
            @pagination-source-change="onTasklistPaginationSourceChange"
            @pagination-reset="onTasklistPaginationReset"
          />

        </v-card-text>
      </v-card>

      <Pagination
        v-if="!isHorizonView && tasklistPaginationList.length > 0"
        :key="tasklistPaginationKey"
        :list="tasklistPaginationList"
        :itemsperpage="tasklistItemsPerPage"
        :page="tasklistPaginationPage"
        :out-of-range-page="snapUnloadedTasklistPageToFirst ? 'first' : 'last'"
        data-testid="tasklist__pagination"
        @paginated-data="onTasklistPaginatedData"
      />

      <TasklistDrawers
        v-if="isHorizonView"
        ref="horizonTasklistDrawers"
        :project-id="id"
        :metadata="metadata"
        :selected-tasks="selectedTasks"
        :page-loading="loading"
        :custom-columns="customColumns"
        :due-date-min-date="dueDateMinDate"
        :due-date-max-date="dueDateMaxDate"
        :user-map="userMap"
        :user-details="userDetails"
        :transaction-matching-list="transactionMatchingList"
        :bot-list="botlist"
        :lease-schedule-items="leaseScheduleItems"
        :location-list="locationList"
        :category-acc="categoryAcc"
        :user-type="userType"
        :get-names-by-ids="getNamesByIds"
        :get-location-by-id="getLocationById"
        :workpaper-file-names-list="[]"
        v-bind="tasklistDrawerBindings"
        data-testid="tasklist__horizon-drawers"
      />
  </div>
</template>

<script>
import './TasksLandingPage.scss';
import { mapState } from 'vuex';
import TasklistView from "./TaskList/TasklistView.vue";
import TasklistDrawers from "./TaskList/TasklistDrawers.vue";
import TasklistPageHeader from "./TaskList/TasklistPageHeader.vue";
import TasksViewToolbarShell from "./TaskList/components/TasksViewToolbarShell/TasksViewToolbarShell.vue";
import { TasklistToolbar } from "./TaskList/components";
import HorizonLanding from "./Horizon/HorizonLanding.vue";
import HorizonToolbar from "./Horizon/HorizonToolbar.vue";
import { Pagination } from "../../components";
import { HORIZON_VIEWS } from "../../utils/horizonConstants";
import { openTaskDetailPage, persistTasklistUserType, clearPersistedTasklistUserType, shouldForceTasklistRefresh } from "../../utils/tasklistUtils";
import { shouldCancelTasklistOnLeave } from "../../utils/tasklistProgressiveLoad";
import { buildDetailNavigationSnapshot, getCurrentPageFromState } from "../../utils/taskDetailNavigation";
import { formatCustomColumns, formatDateToDMY, validateDueDateRange, handleCustomColumnHeadersUtil, applyCustomColumnSchema, checkForNotNull, calculateScheduleAmount, calculateExplainedDifference, calculateUnexplainedDifference, extractFileIdsFromTask, hasDownloadableFiles, getStageDisplay, isTaskBotLinked, applyReconColumnVisibilityForTaskTypeFilter, getCustomColumns } from "../../utils/utils";
import {
  suppressLegacyDueDaysColumn,
  isValidRejectionRemark, buildChecklistStatusRemarkPayload, formatTasklistYesNoExportCell,
  hasVisibleTasklistDataColumn,
  restoreTasklistColumnVisibility,
  mergeTasklistColumnLayouts,
  buildTasklistColumnMergeBase,
  buildTasklistHeadersFromLayout,
  applyUserTypeHeaderVisibility,
  isSameTasklistHeaders,
} from "../../utils/tasklistHelpers";
import { getTasklistTableHeaders, TASK_STAGES, CYCLE_MESSAGES , ERROR_KEYS_CYCLE_TASKLIST,LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,LEGACY_TASK_TYPE_LABEL_CHECKLIST, TABLE_OFFSET_SMALL_SCREEN, TABLE_OFFSET_LARGE_SCREEN, LARGE_SCREEN_HEIGHT_THRESHOLD, MAX_DUE_DATE_DAYS, ACTIVITY_LOG_TYPES, SIGN_OFF, CHECKLIST_STATUS_ACTION, REJECTION_REMARK, AUTO_APPROVE, EVIDENCE_REQUIRED, coerceEvidenceRequired, isEvidenceRequiredEnabled, TASKLIST_LOAD_STATUS, TASKLIST_LOAD_MESSAGES } from '../../utils/constants';
import { CHECKLIST_VALIDATION_MESSAGES, canonicalizeBulkTaskHeader, findDuplicateValues, duplicateInRoleMessage } from '../../utils/bulkUploadValidationMessages';
import {
  mergeCycleTaskRowErrors,
  formatBulkUpdateRowErrorMessages,
  parseCycleTaskValidateResult,
} from '../../utils/cycleTaskBulkUpload';
import { canonicalLocationKey } from '../../utils/locationName';
import { getCycleDueDateWindow } from '../../utils/workingDays';

const IMPORT_BLOCKED_CUSTOM_KEYS = new Set([
  'manager',
  'state',
  'customfields',
  'enforceThreshold',
]);

const BULK_EDIT_FILE_HEADINGS = [
  '__taskId__',
  'Reference',
  'Task Name',
  'Due Date (dd/mm/yyyy)',
  'Category',
  'Sub Category',
  'Purpose',
  'Instruction',
  'Priority',
  'Preparer',
  'Approver',
  SIGN_OFF.APPROVER_LABEL,
  'Frequency',
  'Linked Gls',
  'Location',
  'Assertions',
  'Enforce Threshold',
  'Auto-Approve',
  'No. of Evidences',
  'Evidence Required',
];
let styledxlsx = require("sheetjs-style");

// beforeRouteEnter runs before created() but its next(vm) callback runs after the
// child TasklistView is created, so created() reads the back-nav decision from here.
let pendingTasklistEnter = null;

function consumePendingTasklistEnter(route) {
  const enter = pendingTasklistEnter;
  pendingTasklistEnter = null;
  return enter && enter.fullPath === route?.fullPath ? enter : null;
}

export default {
  name: "TasksLandingPage",
  // Expose constant for template interpolation via $options
  MAX_DUE_DATE_DAYS,
  ACTIVITY_LOG_TYPES,
  data: function () {
    return {
      // Flag to track if component is still active (prevents API calls during/after navigation away)
      isComponentActive: true,
      // Set when beforeRouteEnter/Update or mounted starts a tasklist load (avoids double-fetch).
      tasklistLoadStarted: false,
      tasklistRetrying: false,
      // Track if table has active filters (for external clear button)
      hasActiveTableFilter: false,
      // Page-end pagination (same pattern as Cycles.vue)
      tasklistPaginationList: [],
      tasklistItemsPerPage: 50,
      tasklistPaginationKey: 0,
      // First Pagination emit uses the page saved when the row was opened.
      tasklistPaginationPage: 1,
      // Track window height for responsive table sizing
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      right: "financialclose_analysis",
      page_name: "financialclose_project_checklist",
      custom_layout_success: false,
      columnOrderChanged: false,
      /** Pre-checklist-filter visibility for recon columns (transient, not persisted). */
      reconColumnVisibilitySnapshot: {},
      metadata: {},
      loading: false,
      viewMode: false,
      viewModeToggleReady: false,
      horizonToolbarBridge: {},
      alltaskassigned: true,
      botlist: this.$constants.TASK_LIST_CONSTANTS.BOT_LIST,
      leaseScheduleItems: this.$constants.TASK_LIST_CONSTANTS.LEASE_SCHEDULE_ITEMS,
      // Make constant available to template
      LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
      LEGACY_TASK_TYPE_LABEL_CHECKLIST,
      localCustomColumns: [],
      customColumnSchema: [],
      configureColumns: [
        "Priority",
        "Category",
        "Sub Category",
        "Preparer",
        "Approver",
        "Linked GL(s)",
        "Information requests",
        "Purpose",
        "Instruction",
        "Location",
      ],
      tableHeaders: [
        {
          text: "Ref",
          value: "ref",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: "Task type",
          value: "task_type",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: "Task Name",
          value: "description",
          visible: true,
          filterable: true,
          sortable: true,
          linkHover: true,
        },
        {
          text: "Status",
          value: "stage",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Action",
          value: "upload",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "TB Amount",
          value: "__tbtotals",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Recon amount",
          value: "scheduleAmount",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Explained difference",
          value: "explainedDifference",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Unexplained difference",
          value: "difference",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Category",
          value: "category",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: "Sub category",
          value: "subcategory",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: this.$store.state.LocationHeader || "Location",
          value: "location",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Priority",
          value: "priority",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: "Tags",
          value: "tags",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "No. of Evidences",
          value: "no_of_evidence",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Created on",
          value: "created_at",
          visible: true,
          filterable: true,
          sortable: true,
        },
        {
          text: "Preparer",
          value: "preparer",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Approver",
          value: "approver",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Information request",
          value: "inforrequest",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
          text: "Action",
          value: "action",
          visible: true,
          filterable: false,
          sortable: false,
        },
      ],
      tableHeadersfilter: suppressLegacyDueDaysColumn(
        getTasklistTableHeaders(this.$store.state.LocationHeader || "Location")
      ),
      checklist: [],
      id: "",
      userid: "",
      clientId: "",
      // viewtaskdailog: false,
      viewtaskdata: [],
      search: "",
      menuOpen: false,
      fileNamesList: [],
      userType: this.$store?.state?.tasklistUserType,
      searchcolumn: "",
      selectedTasks: [],
      tasklistDataCopy: [],
      tblinkdialogitems: [],
      tblinkdialog: false,
      tblinkdialogchecklistdata: {},
      selectedTabStage: null,
      categoryAcc: [],
      subcategoryAcc: [],
      coaCategoryAcc: [],
      coaSubcategoryAcc: [],
      formdata: {},
      overDueDate: false,
      taskMasterCustomColumns: [],
      cycleType:"FBC",
      bulkactions: 0,
      bulkselectedlocations: [],
      showSearch: false,
      searchQuery: "",
      conversationList: [],
      queryMsg: "",
      sendforreviewdialogbulk: false,
      fileuplaoded: null,
      fileremark: "",
      uploadiwithoutfile: false,
      bulkactionuserselected: false,
      inforeqstage: "",
      file: null,
      fileprocessing: {},
      flag: false,
      uploaddata: [],
      dataerror: [],
      dialog: false,
      showdatawitherror: false,
      errorsonly: [],
      activitylogconfig: {
        checklistedit: {
          icon: "mdi-pencil",
          color: "fbcPrimary",
          displaytext: "Checklist Updated",
        },
        bulkapprove: {
          icon: "mdi-tag-check-outline ",
          color: "fbcPrimary",
          displaytext: "Bulk Approve",
        },
        bulkreject: {
          icon: "mdi-close-box-multiple-outline  ",
          color: "fbcPrimary",
          displaytext: "Bulk Reject",
        },
        checklistsentforreview: {
          icon: "mdi-file-eye",
          color: "fbcPrimary",
          displaytext: "Checklist Sent For Review",
        },
        fileupload: {
          icon: "mdi-file-upload",
          color: "fbcPrimary",
          displaytext: "File Upload",
        },
      },
      resetFlag: false,
      defaultTableHeadersfilter: [],
      defaultTableFilterHeader: {},
      pendingTableFilter: null,
      restoreListViewFromBackNav: false,
      // Back from task detail keeps the saved page while rows are still loading.
      // A refresh on this page clears it so an unloaded page starts at page 1.
      preserveTasklistPageOnPartialLoad: false,
      /** created() applied the full cached state; the next load pass skips unchanged filter re-applies. */
      tasklistHydratedFromCache: false,
      forceClearListViewFilter: false,
      page_details: {},
      priorityList: [
        { name: "High", id: "High" },
        { name: "Medium", id: "Medium" },
        { name: "Low", id: "Low" },
      ],
      bulkEditDialogloading:false,
      bulkEditFileDataFile:null,
      resetbulkEditFile:false,  
      bulkEditData:{},
      bulkEditFileDataList:[],
      bulkEditFileDataerror:false,
      errorsonlyEditBulk:[],
      dataerroreditBulk:[],
      flageditBulk:false,
      showdatawitherrorEditBulk:false,
      bulkEditTableHeaders: [...this.$constants.TASK_LIST_CONSTANTS.BULK_EDIT_HEADERS_CLOSE_CYCLE],
      bulkEditErrorResponse: [],
      defaultCloneTableHeadersfilter:[],
      checkListErrorResponse: '',
      sendforreviewloadingbulk: false,
      selectedFilter: 'all',
      userDetails:{ activeUsers: [], activeUserIds: [] }
    };
  },
  components: {
    TasklistView,
    TasklistDrawers,
    TasklistPageHeader,
    TasksViewToolbarShell,
    TasklistToolbar,
    HorizonLanding,
    HorizonToolbar,
    Pagination,
  },
  beforeRouteEnter(to, from, next) {
    const isFromTaskDetails = !shouldForceTasklistRefresh(from);
    pendingTasklistEnter = { fullPath: to.fullPath, isFromTaskDetails };
    next(vm => {
      vm.restoreListViewFromBackNav = isFromTaskDetails;
      if (isFromTaskDetails) vm.preserveTasklistPageOnPartialLoad = true;
      // Seed column filters from Vuex before child table mounts (avoids empty selectfilter wipe race)
      vm.seedPendingFilterFromListViewState(to.params?.id);
      // From task details → use cached data; from TB/IR/other tabs → force refresh
      vm.tasklistLoadStarted = true;
      vm.refreshData(shouldForceTasklistRefresh(from));
    });
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.id === from.params.id) {
      next();
      return;
    }
    // Cycle switched — persist prior cycle, then reload for the new one.
    // Note: base.vue keys router-view on fullPath, so this instance may be destroyed
    // right after; mounted() on the new instance also ensures a load in that case.
    this.saveListViewState();
    this.restoreListViewFromBackNav = false;
    this.preserveTasklistPageOnPartialLoad = false;
    this.metadata = {};
    this.id = to.params.id || '';
    this.userType = null;
    this.$store.commit('setTasklistUserType', null);
    clearPersistedTasklistUserType(from.params.id);
    this.seedPendingFilterFromListViewState(to.params.id);
    next();
    this.$nextTick(() => {
      if (!this.isComponentActive) return;
      this.tasklistLoadStarted = true;
      this.refreshData(true);
    });
  },
  // Prevent API calls when navigating away from this component
  beforeRouteLeave(to, from, next) {
    // Persist filters before destroy so remount / refresh can restore them
    this.saveListViewState();
    this.isComponentActive = false;
    // Task detail of this cycle keeps the stored rows and any in-flight pages.
    // Client switch and leaving Financial Close abort; a partial list reloads later.
    if (shouldCancelTasklistOnLeave(to, from)) {
      this.$store.dispatch('tasks/cancelTasklistLoad', { cycleId: from.params.id || this.id });
    }
    this.userType = null;
    this.$store.commit('setTasklistUserType', null);
    clearPersistedTasklistUserType(from.params.id || this.id);
    next();
  },
  created() {
    this.viewMode = this.$route.query.view === HORIZON_VIEWS.HORIZON;
    this.id = this.$route.params.id || '';
    this.tasklistPaginationPage = this.detailNavigationPageForProject(this.id);
    this.userid = this.$store.state.user?.id || '';
    this.clientId = this.$store.state.client || '';
    this.persistTasklistView();
    // Default headers must be captured before any hydrate replaces tableHeadersfilter.
    // (do NOT call refreshData here - it's handled by beforeRouteEnter / mounted)
    this.defaultTableHeadersfilter = JSON.parse(
      JSON.stringify(this.tableHeadersfilter)
    );
    this.defaultCloneTableHeadersfilter = this.mergeColumns(this.defaultTableHeadersfilter, this.tableHeadersfilter);
    this.defaultTableFilterHeader = {};
    if (consumePendingTasklistEnter(this.$route)?.isFromTaskDetails) {
      this.restoreListViewFromBackNav = true;
      this.preserveTasklistPageOnPartialLoad = true;
    }
    // Seed early so TasklistView mounts with selectfilter already populated
    this.seedPendingFilterFromListViewState(this.$route.params.id);
    // enforceHorizonViewAccess otherwise runs after fetchFormDataV3 — formDataV3 is not loaded yet on refresh
    if (!this.restoreListViewFromBackNav || !this.hydrateTasklistFromCache()) {
      this.hydrateTasklistFromStore();
    }
  },

  methods: {
    /**
     * Sync ?view=horizon in the address bar without vue-router navigation.
     * base.vue keys router-view on $route.fullPath — router.replace remounts Tasklist and clears metadata.
     */
    syncViewModeQueryParam(isHorizon) {
      const query = { ...this.$route.query };
      if (isHorizon) {
        query.view = HORIZON_VIEWS.HORIZON;
      } else {
        delete query.view;
      }
      const { href } = this.$router.resolve({ path: this.$route.path, query });
      window.history.replaceState(window.history.state, '', href);
    },
    syncUserTypeQueryParam(userType) {
      const query = { ...this.$route.query, userType };
      const { href } = this.$router.resolve({ path: this.$route.path, query });
      window.history.replaceState(window.history.state, '', href);
    },
    syncHorizonViewUserType(userType) {
      if (!this.id || !userType) return;
      const current = this.$store.getters['tasks/getHorizonViewState'](this.id);
      if (current?.userType === userType) return;
      this.$store.dispatch('tasks/saveHorizonViewState', {
        projectId: this.id,
        partial: {
          userType,
          activeHorizonFilters: {},
          selectedCell: null,
        },
      });
    },
    onHorizonRoleChange(userType) {
      if (userType === this.userType) return;
      this.filterListByUser(userType);
    },
    onHorizonDueDateUpdated() {
      this.refreshData();
    },
    onHorizonToolbarFilterChange(filters) {
      this.$refs.horizonLanding?.onFilterChange?.(filters);
    },
    onHorizonToolbarDateRangeChange(range) {
      this.$refs.horizonLanding?.onHorizonDateRangeChange?.(range);
    },
    onHorizonToolbarStateChange(props) {
      this.horizonToolbarBridge = props || {};
    },
    getAvailableUserTypes() {
      return this.hasManagerRole
        ? ['manager', 'preparer', 'approver']
        : ['preparer', 'approver'];
    },
    /** Keep in-session role on in-app refresh; resolve defaults only when none is active yet. */
    resolveLoadedUserType() {
      const available = this.getAvailableUserTypes();
      if (this.userType && available.includes(this.userType)) {
        return this.userType;
      }
      return this.resolveInitialUserType();
    },
    resolveInitialUserType() {
      const projectId = this.$route.params.id || this.id;
      const available = this.getAvailableUserTypes();

      // Returning from task details — keep the role the user had on this checklist.
      if (this.restoreListViewFromBackNav) {
        const savedListState = projectId
          ? this.$store.getters['tasks/getListViewState'](projectId)
          : null;
        const checklistPreferred = [this.userType, savedListState?.userType].find(
          (role) => role && available.includes(role),
        );
        if (checklistPreferred) return checklistPreferred;
      }

      // Hard refresh / cycle entry / cycle switch: default persona for this user.
      return this.hasManagerRole ? 'manager' : 'preparer';
    },
    applyResolvedUserType(resolvedRole) {
      if (!resolvedRole) return;
      const checklistId = this.$route.params.id || this.id;
      if (this.userType === resolvedRole) {
        this.syncUserTypeQueryParam(resolvedRole);
        this.$store.commit('setTasklistUserType', resolvedRole);
        persistTasklistUserType(checklistId, resolvedRole);
        return;
      }
      this.filterListByUser(resolvedRole);
    },
    /**
     * Horizon is only available when formDataV3.horizonViewEnabled is true.
     * Reset view mode and URL if the flag is off (e.g. ?view=horizon on a non-Horizon client).
     * Skips while formDataV3 is still loading so a refresh with ?view=horizon is not cleared early.
     */
    enforceHorizonViewAccess() {
      if (!this.$store.getters['tasks/getFormDataV3']) return;
      if (this.viewMode && !this.horizonViewEnabled) {
        this.viewMode = false;
        this.syncViewModeQueryParam(false);
      }
    },
    /** Keep local list copies in step while later tasklist pages are appended. */
    syncProgressiveTasklistRows() {
      if (!this.isComponentActive || this.tasklistLoad.cycleId !== this.id) return;
      const storeData = this.$store.getters['tasks/getTaskListData'] || {};
      if (storeData.metadata?._id !== this.id || !Array.isArray(storeData.checklist)) return;
      if (storeData.checklist === this.checklist) return;
      this.checklist = storeData.checklist;
      this.tasklistDataCopy = [...storeData.checklist];
    },
    async retryTasklistLoad() {
      if (this.tasklistRetrying) return;
      this.tasklistRetrying = true;
      try {
        await this.$store.dispatch('tasks/retryTasklistPages', { checklistId: this.id });
      } catch (err) {
        this.$store.commit('sbError', err?.message || TASKLIST_LOAD_MESSAGES.PAGE_FAILED);
      } finally {
        this.tasklistRetrying = false;
      }
    },
    hydrateTasklistFromStore() {
      const checklistId = this.$route.params.id || this.id;
      if (!checklistId) return;
      const storeData = this.$store.getters['tasks/getTaskListData'] || {};
      if (storeData.metadata?._id !== checklistId) return;
      if (!this.metadata?._id) {
        this.metadata = storeData.metadata || {};
      }
      if (!this.checklist?.length && storeData.checklist?.length) {
        this.checklist = storeData.checklist;
      }
      if (!this.tasklistDataCopy?.length && storeData.checklist?.length) {
        this.tasklistDataCopy = [...storeData.checklist];
      }
    },
    /**
     * Back from task detail with this cycle's list, layout and formdata all in Vuex:
     * apply final columns, saved filters, role and users before TasklistView mounts
     * so it renders once. The later refreshData(false) pass then finds nothing changed.
     * @returns {boolean} false when something is not cached (caller falls back).
     */
    hydrateTasklistFromCache() {
      const checklistId = this.$route.params.id || this.id;
      const taskListObj = this.$store.getters['tasks/getTaskListData'] || {};
      if (!checklistId || taskListObj.metadata?._id !== checklistId) return false;
      if (!this.$store.getters['tasks/hasCustomLayout'](this.page_name)) return false;
      const formData = this.$store.getters['tasks/getFormDataV3'];
      if (!formData) return false;

      this.enforceHorizonViewAccess();
      if (!this.isHorizonView) {
        // Before the role resolves: filterListByUser saves overDueDate / tab to listViewState.
        this.restoreListChromeFromState();
      }
      this.applyCachedTasklistData(taskListObj, {
        layoutLoaded: true,
        layoutData: this.$store.getters['tasks/getCustomLayout'](this.page_name),
        useSeededFilter: true,
      });
      this.setActiveUserDetails((formData.users || []).filter((u) => u.isActive));
      this.tasklistHydratedFromCache = true;
      return true;
    },
    /**
     * Apply a cached getAllTasks result plus its custom layout. Shared by the sync
     * back-nav hydrate and loadTasklistData's cached branch; assigns only what changed.
     * @param {Object} taskListObj - tasks/getTaskListData for this cycle
     * @param {{ layoutLoaded?: boolean, layoutData?: Object|null, useSeededFilter?: boolean }} options
     *   useSeededFilter: TasklistView is not mounted yet; use the seeded filter for recon columns.
     */
    applyCachedTasklistData(taskListObj, { layoutLoaded = false, layoutData = null, useSeededFilter = false } = {}) {
      this.assignIfChanged('metadata', taskListObj.metadata || {});
      this.cycleType = (taskListObj.checklist?.[0]?.cycle_type) || "FBC";
      this.assignIfChanged('checklist', taskListObj.checklist || []);
      this.assignIfChanged('coaCategoryAcc', taskListObj.coaCategoryAcc || [], true);
      this.assignIfChanged('coaSubcategoryAcc', taskListObj.coaSubcategoryAcc || [], true);
      this.assignIfChanged('categoryAcc', taskListObj.categoryAcc || [...this.coaCategoryAcc], true);
      this.assignIfChanged('subcategoryAcc', taskListObj.subcategoryAcc || [...this.coaSubcategoryAcc], true);

      const customColumnSchema = taskListObj.customColumnSchema || [];
      this.assignIfChanged('customColumnSchema', customColumnSchema, true);
      const { localCustomColumns, taskMasterCustomColumns, tableHeadersfilter } = handleCustomColumnHeadersUtil(taskListObj.customColumns, this.tableHeadersfilter, customColumnSchema);
      this.assignIfChanged('localCustomColumns', localCustomColumns || [], true);
      this.assignIfChanged('taskMasterCustomColumns', taskMasterCustomColumns, true);

      if (layoutLoaded) {
        this.custom_layout_success = true;
        this.seedPendingFilterFromLayout(layoutData);
        let tableFilterHeader;
        if (useSeededFilter) {
          tableFilterHeader = this.isHorizonView ? {} : (this.pendingTableFilter || {});
        }
        this.applyCustomLayout(layoutData, tableFilterHeader);
      } else {
        this.custom_layout_success = false;
        this.setTableHeaders(tableHeadersfilter);
      }

      this.tasklistDataCopy = Array.isArray(taskListObj.checklist) ? [...taskListObj.checklist] : [];
      this.setPageDetails(this.tableHeadersfilter);

      this.$store.commit('tasks/SET_MANAGER_SUMMARY', taskListObj.summary?.manager || {});
      this.$store.commit('tasks/SET_PREPARER_SUMMARY', taskListObj.summary?.preparer || {});
      this.$store.commit('tasks/SET_APPROVER_SUMMARY', taskListObj.summary?.approver || {});

      this.applyResolvedUserType(this.resolveLoadedUserType());
    },
    /**
     * Assign a data key only when the value differs, so an unchanged background
     * pass does not re-render children. `deep` compares small plain arrays by JSON.
     */
    assignIfChanged(key, next, deep = false) {
      const prev = this[key];
      if (prev === next) return;
      if (deep && JSON.stringify(prev) === JSON.stringify(next)) return;
      this[key] = next;
    },
    setTableHeaders(next) {
      if (isSameTasklistHeaders(this.tableHeadersfilter, next)) return;
      this.tableHeadersfilter = next;
    },
    setPageDetails(layout) {
      const current = this.page_details || {};
      if (current.cycle_id === this.id && current.pagename === this.page_name && current.layout === layout) {
        return;
      }
      this.page_details = { cycle_id: this.id, pagename: this.page_name, layout };
    },
    setActiveUserDetails(activeUsers) {
      const current = this.userDetails.activeUsers || [];
      if (current.length === activeUsers.length && current.every((user, idx) => user === activeUsers[idx])) {
        return;
      }
      this.$set(this.userDetails, 'activeUsers', activeUsers);
      this.$set(this.userDetails, 'activeUserIds', activeUsers.map(u => u._id).filter(Boolean));
    },
    /**
     * Persist List vs Horizon so task-detail back navigation can restore view without a query param.
     */
    persistTasklistView(isHorizon = this.isHorizonView) {
      const checklistId = this.$route.params.id || this.id;
      if (!checklistId) return;
      this.$store.commit('tasks/SET_TASKLIST_VIEW', {
        checklistId,
        view: isHorizon ? HORIZON_VIEWS.HORIZON : HORIZON_VIEWS.LIST,
      });
    },
    /**
     * Handle view mode change between List and Horizon — hydrate Horizon from loaded data (no API refetch).
     * Switching back to List remounts TasklistView; re-sync filters/pagination after mount.
     */
    async onViewModeChange(mode) {
      const nextMode = mode === true || mode === false ? mode : this.viewMode;
      if (nextMode === true && !this.horizonViewEnabled) {
        return;
      }
      const isHorizon = nextMode === true;
      this.syncViewModeQueryParam(isHorizon);
      this.persistTasklistView(isHorizon);

      if (isHorizon) {
        this.ensureStoreHasChecklistForHorizon();
        await this.$nextTick();
        this.syncHorizonAfterDataLoad();
        return;
      }

      // List remount: restore filters and force Pagination to re-emit page rows.
      await this.$nextTick();
      this.syncTasklistViewAfterLoad();
      if (this.tasklistPaginationList.length > 0) {
        this.tasklistPaginationKey += 1;
      }
    },
    ensureStoreHasChecklistForHorizon() {
      const storeData = this.$store.getters['tasks/getTaskListData'] || {};
      if (Array.isArray(storeData.checklist) && storeData.checklist.length > 0) {
        return;
      }
      const source = this.checklist?.length
        ? this.checklist
        : (this.tasklistDataCopy?.length ? this.tasklistDataCopy : []);
      if (!source.length || !this.metadata?._id) {
        return;
      }
      this.$store.commit('tasks/SET_TASKLIST_DATA', {
        ...storeData,
        metadata: this.metadata,
        checklist: JSON.parse(JSON.stringify(source)),
      });
    },
    onHorizonLandingMounted() {
      this.$refs.horizonLanding?.ensureHorizonReady?.();
    },
    async waitForHorizonLandingRef(maxAttempts = 15) {
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        await this.$nextTick();
        const ref = this.$refs.horizonLanding;
        if (ref) {
          return ref;
        }
      }
      return null;
    },
    async syncHorizonAfterDataLoad() {
      if (!this.horizonViewEnabled || !this.isHorizonView) {
        return;
      }
      const ref = await this.waitForHorizonLandingRef();
      ref?.ensureHorizonReady?.();
    },
    /**
     * Clear table filters via DataTableAction ref
     */
    clearTableFilter() {
      const view = this.$refs.tasklistView;
      if (!view) return;
      this.forceClearListViewFilter = true;
      view.clearTableFilter();
    },
    /** DataTableAction clear-item — allow empty filter snapshot to persist. */
    onTasklistTableFilterClear() {
      this.forceClearListViewFilter = true;
    },
    /**
     * Load saved column filters into pendingTableFilter before TasklistView mounts.
     * Prevents DataTableAction from initializing with {} and wiping listViewState.
     */
    seedPendingFilterFromListViewState(projectId) {
      const id = projectId || this.$route.params.id || this.id;
      if (!id) return;
      const saved = this.$store.getters['tasks/getListViewState'](id);
      const filter = saved?.tableFilterHeader;
      if (filter && typeof filter === 'object' && Object.keys(filter).length > 0) {
        this.pendingTableFilter = JSON.parse(JSON.stringify(filter));
      }
    },
    syncTasklistViewAfterLoad() {
      // TasklistView already mounted with the cached filters: only re-apply on a real difference.
      const skipUnchanged = this.tasklistHydratedFromCache;
      this.tasklistHydratedFromCache = false;
      const view = this.$refs.tasklistView;
      if (!view) return;

      const pending = this.pendingTableFilter;
      this.pendingTableFilter = null;

      // Apply non-empty pending filter (seeded from listViewState or URL). Never wipe with {}.
      if (pending != null && typeof pending === 'object' && Object.keys(pending).length > 0) {
        const snapshot = { tableFilterHeader: pending };
        if (!skipUnchanged || !view.isListViewSnapshotApplied(snapshot)) {
          view.applyListViewSnapshot(snapshot);
        }
      }

      view.applyUrlQueryFilters();
      this.applyListViewState({ skipUnchanged });
      this.syncReconColumnVisibilityForTaskTypeFilter();
    },
    /** Hide recon columns when task-type filter is checklist-only; restore when filter clears. */
    syncReconColumnVisibilityForTaskTypeFilter(tableFilterHeader) {
      const next = this.resolveTasklistHeaderVisibility(this.tableHeadersfilter, tableFilterHeader);
      if (next !== this.tableHeadersfilter) {
        this.tableHeadersfilter = next;
      }
    },
    /**
     * Recon-column visibility for the task-type filter, then keep at least one data
     * column visible. Mutates `headers` in place; returns a new array only when
     * visibility had to be restored from defaults.
     * @param {Array} headers
     * @param {Object} [tableFilterHeader] - defaults to the mounted table's filter
     * @returns {Array}
     */
    resolveTasklistHeaderVisibility(headers, tableFilterHeader) {
      const filter = tableFilterHeader
        ?? this.$refs.tasklistView?.tableFilterHeader
        ?? {};
      const { visibilitySnapshot } = applyReconColumnVisibilityForTaskTypeFilter(
        headers,
        filter,
        this.reconColumnVisibilitySnapshot
      );
      this.reconColumnVisibilitySnapshot = visibilitySnapshot;
      if (hasVisibleTasklistDataColumn(headers)) {
        return headers;
      }
      const restored = restoreTasklistColumnVisibility(headers, this.defaultTableHeadersfilter);
      const { visibilitySnapshot: restoredSnapshot } = applyReconColumnVisibilityForTaskTypeFilter(
        restored,
        filter,
        this.reconColumnVisibilitySnapshot
      );
      this.reconColumnVisibilitySnapshot = restoredSnapshot;
      return restored;
    },
    /** Persist list chrome to Vuex listViewState (keyed by close-cycle projectId). */
    saveListViewState(partial = {}) {
      const projectId = this.$route.params.id || this.id;
      if (!projectId) return;
      // Only include table snapshot when list view is mounted — otherwise {} would
      // spread itemsPerPage/tableFilterHeader as undefined and wipe saved filters.
      const listView = this.$refs.tasklistView;
      const snapshot = listView?.getListViewSnapshot?.();
      const payload = {
        userType: this.userType,
        overDueDate: this.overDueDate,
        ...partial,
      };
      if (this.selectedTabStage !== null && payload.selectedTabStage === undefined) {
        payload.selectedTabStage = this.selectedTabStage;
      }
      if (listView && snapshot) {
        if (partial.itemsPerPage === undefined) {
          payload.itemsPerPage = snapshot.itemsPerPage;
        }
        if (partial.tableFilterHeader === undefined) {
          payload.tableFilterHeader = snapshot.tableFilterHeader;
        }
      } else {
        delete payload.itemsPerPage;
        delete payload.tableFilterHeader;
      }
      this.$store.dispatch('tasks/saveListViewState', {
        projectId,
        partial: payload,
      });
    },
    /** Pipeline tab + overdue toggle from listViewState (no table ref needed). */
    restoreListChromeFromState() {
      const projectId = this.$route.params.id || this.id;
      const saved = projectId ? this.$store.getters['tasks/getListViewState'](projectId) : null;
      if (!saved) return null;
      if (saved.selectedTabStage !== undefined) {
        this.selectedTabStage = saved.selectedTabStage;
      }
      if (typeof saved.overDueDate === 'boolean') {
        this.overDueDate = saved.overDueDate;
      }
      return saved;
    },
    /**
     * Restore list chrome from Vuex after data load (filters + page size).
     * @param {{ skipUnchanged?: boolean }} [options] - skip the table snapshot when it already matches
     */
    applyListViewState({ skipUnchanged = false } = {}) {
      const projectId = this.$route.params.id || this.id;
      if (!projectId || this.isHorizonView) return;

      const saved = this.restoreListChromeFromState();
      if (!saved) return;

      const view = this.$refs.tasklistView;
      // Always restore column filters + page size when present (back-nav and refresh)
      if (saved.itemsPerPage || (saved.tableFilterHeader && Object.keys(saved.tableFilterHeader).length > 0)) {
        const snapshot = {
          itemsPerPage: saved.itemsPerPage,
          tableFilterHeader: saved.tableFilterHeader,
        };
        if (!skipUnchanged || !view?.isListViewSnapshotApplied(snapshot)) {
          view?.applyListViewSnapshot(snapshot);
        }
      }

      this.restoreListViewFromBackNav = false;
    },
    onListViewStateChange(snapshot) {
      this.syncReconColumnVisibilityForTaskTypeFilter(snapshot?.tableFilterHeader);
      // Ignore empty snapshots while a non-empty filter is already saved — remount race
      // (DataTableAction init with {}) must not wipe listViewState before restore.
      // Intentional Clear Filter sets forceClearListViewFilter and bypasses this guard.
      const projectId = this.$route.params.id || this.id;
      const incomingFilter = snapshot?.tableFilterHeader || {};
      const incomingEmpty = !incomingFilter || Object.keys(incomingFilter).length === 0;
      if (incomingEmpty && projectId && !this.forceClearListViewFilter) {
        const saved = this.$store.getters['tasks/getListViewState'](projectId);
        const savedFilter = saved?.tableFilterHeader || {};
        if (Object.keys(savedFilter).length > 0) {
          return;
        }
      }
      this.forceClearListViewFilter = false;
      this.saveListViewState({
        itemsPerPage: snapshot?.itemsPerPage,
        tableFilterHeader: snapshot?.tableFilterHeader,
      });
    },
    /** Saved detailNavigationPage for this close cycle, or 1 when none is stored. */
    detailNavigationPageForProject(projectId) {
      const id = projectId || this.$route.params.id || this.id;
      const saved = id ? this.$store.getters['tasks/getListViewState'](id) : null;
      return getCurrentPageFromState(saved);
    },
    onTasklistPaginationSourceChange({ list, itemsPerPage } = {}) {
      const nextList = Array.isArray(list) ? list : [];
      const perPage = typeof itemsPerPage === 'number' && itemsPerPage > 0
        ? itemsPerPage
        : (this.tasklistItemsPerPage || 50);
      if (this.snapUnloadedTasklistPageToFirst && nextList.length) {
        const maxPage = Math.max(1, Math.ceil(nextList.length / perPage));
        if (this.tasklistPaginationPage > maxPage) {
          this.tasklistPaginationPage = 1;
        }
      }
      this.tasklistPaginationList = nextList;
      if (typeof itemsPerPage === 'number' && itemsPerPage > 0) {
        this.tasklistItemsPerPage = itemsPerPage;
      }
      // Ref may not exist yet during TasklistView's immediate watcher; seed on next tick.
      this.$nextTick(() => {
        const view = this.$refs.tasklistView;
        if (!view || this.isHorizonView) return;
        const perPage = this.tasklistItemsPerPage || 50;
        const source = this.tasklistPaginationList;
        if (!source.length) return;
        // Only seed if table still has no rows (avoid clobbering an active page).
        // A saved detailNavigationPage must win over the old hardcoded page 1.
        if (!view.paginatedFilteredItems?.length) {
          const page = Math.max(view.page || this.tasklistPaginationPage || 1, 1);
          const start = (page - 1) * perPage;
          view.setPaginatedData({
            data: source.slice(start, start + perPage),
            page,
          });
        }
      });
    },
    onTasklistPaginationReset() {
      // User filter, column sync, and clear remount Pagination. Back-nav restore does not emit this.
      this.tasklistPaginationPage = 1;
      this.tasklistPaginationKey += 1;
    },
    onTasklistPaginatedData(paginationInfo) {
      const page = paginationInfo?.page;
      if (typeof page === 'number' && page > 0) {
        this.tasklistPaginationPage = page;
        if (this.snapUnloadedTasklistPageToFirst && page === 1) {
          const savedPage = this.detailNavigationPageForProject(this.id);
          if (savedPage > 1) {
            this.saveListViewState({ detailNavigationPage: 1 });
          }
        }
      }
      this.$refs.tasklistView?.setPaginatedData(paginationInfo);
    },
    /** Header refresh. An unloaded page starts at 1; back from task detail does not use this. */
    refreshTasklistFromPage() {
      this.preserveTasklistPageOnPartialLoad = false;
      return this.refreshData();
    },
    onBulkActionUserFromToolbar(type) {
      this.$refs.tasklistView?.openBulkActionUserDialog(type);
    },
    onBulkDueDateFromToolbar() {
      this.$refs.tasklistView?.openBulkDueDateDialog();
    },
    onBulkEditFromToolbar() {
      this.$refs.tasklistView?.openBulkEditDialog();
    },
    getTasklistDrawersRef() {
      return this.$refs.tasklistView?.$refs?.tasklistDrawers
        ?? this.$refs.horizonTasklistDrawers;
    },
    onStatusReportFromHeader() {
      this.getTasklistDrawersRef()?.openStatusReport();
    },
    onBulkManageUserFromDrawer({ action, usertype, userid, changeuserid, selectitems }) {
      this.bulkManageUser(action, usertype, userid, selectitems, changeuserid);
    },
    onRowChangeChecklistStatus({ taskId, type, remark }) {
      this.changeChecklistStatus(taskId, type, remark);
    },
    getStageDisplay,
    isTaskBotLinked,
    hasDownloadableFiles,
    extractFileIdsFromTask,
    isClosedTask(item) {
      return item?.stage === TASK_STAGES.CLOSED;
    },
    getStatusConfig(statusDetail) {
      const constants = this.$constants.TASK_LIST_CONSTANTS;
      const statusConfig = {
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_DUE
        },
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_NOT_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION_NOT_DUE,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_NOT_DUE
        },
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_OVERDUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION_OVERDUE,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_OVERDUE
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_DUE
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_NOT_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL_NOT_DUE,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_NOT_DUE
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_OVERDUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL_OVERDUE,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_OVERDUE
        }
      };
      
      return statusConfig[statusDetail] || {
        icon: constants.STATUS_ICONS.CLOSED,
        color: constants.STATUS_COLORS.CLOSED,
        text: constants.STATUS_DETAILS.CLOSED_TASK
      };
    },
    getColumnValue(column, task) {
    if (column.dataType === "date") {
      return task[column.columnName]
        ? this.$nova.formatDate(task[column.columnName])
        : "";
    }
    return task[column.columnName] || "";
  },

  /**
   * Handle Apply button click from CustomizeColumnMenu
   * Updates column visibility locally (session only)
   * @param {Array} headers - Updated headers array
   */
  getTasklistColumnMergeBase() {
    return buildTasklistColumnMergeBase(
      this.defaultTableHeadersfilter,
      this.defaultCloneTableHeadersfilter,
      this.tableHeadersfilter
    );
  },
  handleApplyColumns(headers) {
    const mergedLayout = mergeTasklistColumnLayouts(
      this.getTasklistColumnMergeBase(),
      suppressLegacyDueDaysColumn(headers)
    );
    if (!hasVisibleTasklistDataColumn(mergedLayout)) {
      if (!hasVisibleTasklistDataColumn(this.tableHeadersfilter)) {
        this.syncReconColumnVisibilityForTaskTypeFilter();
        this.$store.commit('sbWarning', 'Default columns have been restored.');
      } else {
        this.$store.commit('sbWarning', 'Select at least one column to display.');
      }
      return;
    }
    this.reconColumnVisibilitySnapshot = {};
    this.tableHeadersfilter = JSON.parse(JSON.stringify(mergedLayout));
    this.syncReconColumnVisibilityForTaskTypeFilter();
    this.menuOpen = false;
    this.$nextTick(() => {
      this.$refs.tasklistView?.scheduleStickyUpdate?.();
    });
  },

  /**
   * Handle Save Columns button click from CustomizeColumnMenu
   * Persists column preferences to database using Vuex saveCustomLayout action
   * @param {Object} payload - { headers, pageName }
   */
  async handleSaveColumns({ headers, pageName }) {
    const mergedLayout = mergeTasklistColumnLayouts(
      this.getTasklistColumnMergeBase(),
      suppressLegacyDueDaysColumn(headers)
    );
    if (!hasVisibleTasklistDataColumn(mergedLayout)) {
      this.$store.commit('sbWarning', 'Select at least one column to display.');
      return;
    }
    this.reconColumnVisibilitySnapshot = {};
    this.tableHeadersfilter = JSON.parse(JSON.stringify(mergedLayout));
    this.defaultCloneTableHeadersfilter = JSON.parse(JSON.stringify(mergedLayout));
    this.syncReconColumnVisibilityForTaskTypeFilter();
    this.loading = true;
    try {
      await this.$store.dispatch('saveCustomLayout', {
        axios: this.axios,
        page: pageName || this.page_name,
        cycleId: this.id,
        layout: mergedLayout,
        filter: this.$refs.tasklistView?.tableFilterHeader || {}
      });
      this.page_details = { cycle_id: this.id, pagename: this.page_name, layout: mergedLayout };
      this.columnOrderChanged = false;
      this.menuOpen = false;
    } catch (err) {
      console.error('[Tasklist]: Error saving columns:', err);
    } finally {
      this.menuOpen = false;
      this.loading = false;
    }
  },
  onColumnReorder(headers) {
    this.tableHeadersfilter = suppressLegacyDueDaysColumn(headers);
    this.columnOrderChanged = true;
  },
  async saveColumnOrder() {
    await this.handleSaveColumns({ headers: this.tableHeadersfilter, pageName: this.page_name });
  },
     getAvailableUsers(possibleUsers) {
    const users = Object.values(this.userMap);
    if (users.length === 0) return [];
    if (!Array.isArray(possibleUsers)) possibleUsers = [];
    return users.filter(
      u => !possibleUsers.some(pu => pu._id === u._id) && this.userDetails.activeUserIds.includes(u._id)
    );
  },
     mergeColumns(colim1, colim2) {
      return mergeTasklistColumnLayouts(colim1, colim2);
    },
    checkForNotNull(item, attribute) {
      return checkForNotNull(item, attribute);
    },
    getBotRoute(item) {

      if (item.bot_details?.name === "Lease Schedule") {
        this.$nova.gotoLink({
          path: "/financialclose/automationlease/" + this.id,
        });
      } else if (item.bot_details?.name === "Borrowing Schedule") {
        this.$nova.gotoLink({
          path: "/financialclose/automationborrowingschedule/" + this.id,
        });
      } else if (item.bot_details?.name === "Transaction Matching" || item.tmaid) {
        this.$nova.gotoLink({
          path:
            "/financialclose/automationtransactionmatching/" +
            this.id +
            "/c/" +
            (item.bot_analysis_detail?._id || item.tmaid),
        });
      } else {
        console.log(this.$constants.BOT_NAME +" not found");
      }
    },
    getNamesByIds(ids) {
      if (!Array.isArray(ids)) {
        if (typeof ids === 'string') {
          ids = [ids];
        } else if (ids == null) {
          return [];
        } else {
          return [String(ids)];
        }
      }
      return ids
        .map((id) => this.userMap[id]?.name || id)
        .filter(Boolean);
    },
    // MARKER: TASK_TYPE
    async downloadExcelTasklist(selectedItem){
      try {
        const selectIds = selectedItem.map(item => item._id);
        
        console.log('[downloadExcelTasklist] Starting download with IDs:', selectIds);

        const response = await this.$store.dispatch('tasks/downloadBulkEditTasklistTemplate', {
          checklistId: this.id,
          taskIds: selectIds,
        });

        // Extract filename from Content-Disposition header with better handling
        let filename = `Tasklist-${new Date().toISOString().split('T')[0]}.xlsx`;
        const contentDisposition = response.headers['content-disposition'];
        
        console.log('[downloadExcelTasklist] Content-Disposition:', contentDisposition);
        
        if (contentDisposition && contentDisposition.trim() !== '') {
          // Handle filename*=UTF-8''encodedname (RFC 5987)
          const filename5987Match = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
          if (filename5987Match && filename5987Match[1]) {
            try {
              filename = decodeURIComponent(filename5987Match[1].trim());
              console.log('[downloadExcelTasklist] Extracted filename (RFC 5987):', filename);
            } catch (e) {
              console.warn('[downloadExcelTasklist] Failed to decode RFC 5987 filename:', e);
            }
          } else {
            // Handle filename="name" or filename=name
            const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim().replace(/^["']|["']$/g, '');
              console.log('[downloadExcelTasklist] Extracted filename (standard):', filename);
            }
          }
        }
          
        // Create download link and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        console.log('[downloadExcelTasklist] Blob URL created:', url);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[downloadExcelTasklist] Download completed successfully');
        this.$store.commit('sbSuccess', 'Tasklist exported successfully');
      } catch (error) {
        console.error('[downloadExcelTasklist] Error exporting tasklist:', {
          message: error?.message,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          errorData: error?.response?.data,
          fullError: error
        });
        this.$store.commit("sbError", error?.response?.data?.message || "Failed to export tasklist. Please try again.");
      }
    },
    downloadTemplate() {
      if (this.id)
        this.$nova.downloadFile(
          "",
          {},
          `/v2/financialclose/analysis/getbulkchecklisttemplate/${this.id ? `${this.id}` : ''}`
        );
      else
        this.$nova.downloadFile(
          "",
          {},
          "/v2/financialclose/masters/checklist/getbulksubmittemplate"
        );
    },

    /**
     * Extracts linked GL codes from a task
     * @param {Object} task - The task object containing __tbtotals data
     * @returns {string} Comma-separated GL codes or empty string
     */
    getLinkedGLsForExport(task) {
      if (!task || !task.__tbtotals || !Array.isArray(task.__tbtotals.items)) {
        return "";
      }
      return task.__tbtotals.items.map(item => item.glcode).join(", ");
    },

    /**
     * Finds a task by ID and extracts its linked GL codes
     * @param {Array} tasks - Array of task objects
     * @param {string} taskId - The task ID to search for
     * @returns {string} Comma-separated GL codes or empty string
     */
    getGLCodeAndNameByChecklistId(tasks, taskId) {
      const task = tasks.find(t => t._id === taskId);
      return this.getLinkedGLsForExport(task);
    },

    // MARKER: TASK_TYPE
    uploadDataBulk() {
      this.bulkEditDialogloading = true;
      this.$store.dispatch('tasks/validateAndWriteCycleTaskList', {
        checklistId: this.id,
        data: this.bulkEditFileDataList,
        mode: 'bulkUpdate',
      })
        .then((result) => {
          if (!result.ok) {
            this.applyBulkValidateRowErrors(result.rowErrors, 'bulkEdit');
            this.bulkEditErrorResponse = formatBulkUpdateRowErrorMessages(result.rowErrors);
            if (!this.bulkEditErrorResponse.length) {
              this.bulkEditErrorResponse = [result.message || 'Bulk update validation failed'];
            }
            throw new Error(result.message || 'Bulk update validation failed');
          }
          const writeData = result.writeData || {};
          if (writeData.status === 'success') {
            this.$store.commit('sbSuccess', 'Tasklist updated successfully');
            this.$refs.tasklistView?.closeBulkEditDialog();
            this.selectedTasks = [];
            this.refreshData();
          } else {
            this.bulkEditErrorResponse = writeData.data || [];
            throw writeData.message || 'Error uploading Tasklist';
          }
        })
        .catch((err) => {
          this.$store.commit('sbError', err.message || err || 'Unknown error!');
          console.log(err);
        })
        .finally(() => {
          this.resetbulkEditFile = !this.resetbulkEditFile;
          this.bulkEditDialogloading = false;
        });
    },
    cleardataBulkEdit(){
      this.bulkEditFileDataList = [];
      this.bulkEditFileDataerror = false;
      this.flageditBulk = false;
      this.dataerroreditBulk = [];
      this.errorsonlyEditBulk = [];
      this.$refs.tasklistView?.closeBulkEditDialog();
      this.bulkEditDialogloading = false;
      this.showdatawitherrorEditBulk = false;
    },
    cleardata() {
      this.file = null;
      this.fileprocessing = {};
      this.flag = false;
      this.uploaddata = [];
      this.dataerror = [];
      this.dialog = false;
      this.loading = false;
      this.showdatawitherror = false;
    },
    // MARKER: TASK_TYPE
    readFile(key) {
      let file = this.file;
      


      if ((file || {}).name && (this.fileprocessing[key] || 0) === 0) {
        let headings = this.$constants.EXPECTED_HEADERS_CLOSE_CYCLE;
        // Add custom columns to headings if they exist
        headings = [...headings, ...(this.importCustomColumns.map((col) => col.dataType === "date" ? `${col.displayText} (dd/mm/yyyy)` : col.displayText) || [])];
        this.loading = true;
        this.fileprocessing[key] = 1;
        let ext = ((file || {}).name || "").split(".").pop();
        Promise.resolve()
          .then(() => {
            if (["xlsx", "xls", "xlsm", "xlsb"].indexOf(ext) === -1)
              throw new Error(CYCLE_MESSAGES.ERROR.UNSUPPORTED_FILE_EXTENSION);
            return this.$nova.readExcelFile(file, "");
          })
          .then(async (dt) => {
            if (
              JSON.stringify((dt[0] || []).map((h) => canonicalizeBulkTaskHeader(h)))
              === JSON.stringify(headings.map((h) => canonicalizeBulkTaskHeader(h)))
            ) {
              let filedata = [];
              let errordata = [];
              this.flag = false;
              for (let i = 0; i < dt.length; i++) {
                if (i > 0 && dt[i].length > 0) {
                  const el = dt[i];
                  let rowdata = {
                    task_type: "",
                    description: el[0],
                    category: el[1],
                    subcategory: el[2],
                  };
                  rowdata.location = (el[3]?.toString() || "base")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.assertions = (el[4] || "")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.purpose = el[5];
                  rowdata.instruction = el[6];
                  rowdata.preparer = (el[7] || "")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.approver = (el[8] || "")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.sign_off_approver = (el[9] || "")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.requires_sign_off = rowdata.sign_off_approver.length > 0;
                  rowdata.tags = (el[10] || "")
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  // Parse Due Days - blank => null, otherwise convert to number (supports signed offsets: -200 to 200)
                  const dueDaysRaw = (el[11] || "").toString().trim();
                  rowdata.duedays = dueDaysRaw && !isNaN(dueDaysRaw) && Number.isFinite(Number(dueDaysRaw)) ? Number(dueDaysRaw) : null;
                  
                  rowdata.glcode = (el[12] || "")
                    .toString()
                    .split(",")
                    .filter((x) => x.replaceAll(/\s/g, ""));
                  rowdata.task_type = rowdata.glcode.length ? this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION : this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
                  rowdata.priority = (el[13] || '').toString().trim();
                  rowdata.no_of_evidence = Number(el[14] || "") || 0;
                  rowdata.evidence_required = coerceEvidenceRequired(el[15]);
                  const enforceText = (el[16] || '').toString().trim().toLowerCase();
                  const enforceThresholdRaw = !['no', 'false', '0'].includes(enforceText);
                  rowdata.enforceThreshold = rowdata.task_type
                    === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION
                    ? enforceThresholdRaw
                    : false;
                  rowdata.auto_approve = ['yes', 'true', '1'].includes((el[17] || '').toString().trim().toLowerCase());
                  let errors = {};
                  // Priority validation: required and must match one of PRIORITY_LIST values (case-insensitive)
                  const allowedPriorities = (this.priorityList || []).map((p) => (p.id || p.name || p.value || p).toString().toLowerCase());
                  if (!rowdata.priority) {
                    errors["priority"] = "Priority is required";
                  } else if (allowedPriorities.length && !allowedPriorities.includes(rowdata.priority.toLowerCase())) {
                    errors["priority"] = "Invalid priority";
                  }
                  // Due Days validation: required, finite number, and in range -200 to 200
                  if (rowdata.duedays === null || rowdata.duedays === '') {
                    errors["duedays"] = "Due Days is required";
                  } else if (isNaN(rowdata.duedays) || !Number.isFinite(rowdata.duedays)) {
                    errors["duedays"] = `'${dueDaysRaw}' is not a valid number`;
                  } else if (rowdata.duedays < -200 || rowdata.duedays > 200) {
                    errors["duedays"] = "Due Days must be between -200 and 200";
                  }
                  if ((rowdata.description || "").toString() === "")
                    errors["description"] = "Description is required";
                  if ((rowdata.category || "").toString() === "")
                    errors["category"] = "Category is required";
                  if ((rowdata.subcategory || "").toString() === "")
                    errors["subcategory"] = "Sub category is required";
                  if ((rowdata.approver || "").toString() === "")
                    errors["approver"] = " Approver is required";
                  if ((rowdata.preparer || "").toString() === "")
                    errors["preparer"] = " Preparer is required";
                  if (rowdata.no_of_evidence < 0 || rowdata.no_of_evidence > 10 || isNaN(rowdata.no_of_evidence)) {
                    errors["no_of_evidence"] = "No. of Evidences must be between 0 and 10";
                  } else if (isEvidenceRequiredEnabled(rowdata.evidence_required) && rowdata.no_of_evidence < 1) {
                    errors["no_of_evidence"] = "When Evidence Required is Yes, No. of Evidences must be between 1 and 10";
                  }
                  if (this.project) delete rowdata.tags;
                  else if ((rowdata.tags || []).length === 0)
                    errors["tags"] = " Tags are required";
                  if (
                    rowdata.preparer.length > 0 &&
                    rowdata.approver.length > 0
                  ) {
                    let preparers = rowdata.preparer;
                    let approvers = rowdata.approver;
                    let duplicates = [];
                    for (const i of preparers) {
                      if (approvers.indexOf(i.trim()) > -1) duplicates.push(i);
                    }
                    if (duplicates.length > 0)
                      errors["preparer"] =
                        "'" +
                        duplicates.join(",") +
                        "' cannot be both preparer and approver";
                  }

                  if (Object.keys(errors).length > 0) this.flag = true;
                  if (this.customColumns.length > 0) {
                    (Array.isArray(this.customColumns) ? this.customColumns : []).forEach(column => {
                      // Account for date suffix when searching for column header
                      let headerIndex = -1;
                      if (column.dataType === 'date') {
                        const headerWithDateSuffix = `${column.displayText} (dd/mm/yyyy)`;
                        headerIndex = headings.indexOf(headerWithDateSuffix);
                      } else {
                        headerIndex = headings.indexOf(column.displayText);
                      }
                      let value = headerIndex >= 0 ? el[headerIndex] : '';
                      rowdata[column.columnName] = value;
                    });
                  }
                  filedata.push(rowdata);
                  errordata.push(errors);
                }
              }
              this.uploaddata = filedata;
              this.dataerror = errordata;
              this.errorsonly = this.dataerror;
              this.dialog = true;

              if (filedata.length && this.id) {
                const validation = await this.$store.dispatch('tasks/validateCycleTaskList', {
                  checklistId: this.id,
                  data: filedata.map((a) => ({ ...a, cycle_type: this.cycle_type })),
                  mode: 'bulkAdd',
                });
                const parsed = parseCycleTaskValidateResult(validation);
                if (!parsed.valid) {
                  this.applyBulkValidateRowErrors(parsed.rowErrors, 'checklist');
                }
              }
            } else
              throw Error(
                "Columns '" +
                  headings.join("','") +
                  "' are required in the same order"
              );
          })
          .catch((err) => {
            this.$store.commit(
              "sbError",
              err.message || err || "Unknown error!"
            );
            console.log(err);
          })
          .finally(() => {
            this.file = null;
            this.fileprocessing[key] = 0;
            this.loading = false;
          });
      }
    },
    // MARKUP_RECON
    readFilesinglebulk(key) {
      console.log(key);

      let files = this.file;
      if (Array.isArray(files) && files.length > 0) {
        this.loading = true;
        Promise.all(
          files.map(async (file, index) => {
            if ((file || {}).name && (this.fileprocessing[index] || 0) === 0) {
              this.fileprocessing[index] = 1;
              let ext = ((file || {}).name || "").split(".").pop();

              // Validate file extension
              if (!["xlsx", "xls", "xlsm", "xlsb"].includes(ext)) {
                throw new Error(
                  `File '${file.name}' has unsupported extension. Only xlsx, xls, xlsm, xlsb are allowed.`
                );
              }

              // Read Excel file
              let dt = await this.$nova.readExcelFile(file, "TOP SHEET");
              let headings = [
                "Task No ",
                "Task description",
                "Location",
                "Particulars",
                "Amount",
                "File Reference",
                "Type"
              ];

              // New set of headings for consildated recon file.
              let headingsConsolidated = [
                "Task No", 
                "Task description (Optional)", 
                "Location",
                "Particulars",
                "Amount",
                "File Reference (Optional)",
                "Type",
                "Standalone Entity (refer # 11 in Instructions)",
                "GL (refer # 12 in Instructions)"
              ]
              // let consolidatedReconFile = false;
              if (JSON.stringify(dt[0] || []) === JSON.stringify(headingsConsolidated)) {
                // consolidatedReconFile = true;
              } else if (JSON.stringify(dt[0] || []) !== JSON.stringify(headings)) {
                throw new Error(
                  `File '${
                    file.name
                  }' is missing required columns or they are not in the correct order. Expected: '${headings.join(
                    "', '"
                  )}'`
                );
              }

            }
          })
        )
          .then(() => {
            console.log("All files validated successfully.");
          })
          .catch((err) => {
            this.$store.commit(
              "sbError",
              err.message || err || "Unknown error!"
            );
            console.log(err);
          })
          .finally(() => {
            this.file = null; // Reset file array
            this.fileprocessing = {}; // Reset processing state
            this.loading = false; // Reset loading state
          });
      } else {
        console.log("No files to validate.");
      }

    },
    // MARKER: TASK_TYPE
    uploadData() {
      this.loading = true;
      this.$store.dispatch('tasks/validateAndWriteCycleTaskList', {
        checklistId: this.id || undefined,
        data: this.uploaddata,
        mode: 'bulkAdd',
      })
        .then((result) => {
          if (!result.ok) {
            this.applyBulkValidateRowErrors(result.rowErrors, 'checklist');
            this.checkListErrorResponse = result.message || 'Bulk add validation failed';
            throw new Error(this.checkListErrorResponse);
          }
          const writeData = result.writeData || {};
          if (writeData.status === 'success') {
            this.$refs.tasklistView?.closeBulkChecklistUploadDialog();
            this.$emit('reload');
            this.$store.commit('sbSuccess', 'Checklist uploaded successfully');
            this.refreshData();
            this.uploaddata = [];
            this.showdatawitherror = false;
            this.dataerror = [];
            this.flag = false;
          } else {
            this.checkListErrorResponse = writeData.message || 'Error uploading Tasklist';
            this.$store.commit('sbError', this.checkListErrorResponse);
          }
        })
        .catch((err) => {
          this.$store.commit('sbError', err.message || err || 'Unknown error!');
          this.resetFlag = !this.resetFlag;
          console.log(err);
        })
        .finally(() => {
          this.resetFlag = !this.resetFlag;
          this.loading = false;
        });
    },

    /**
     * Merge validate rowErrors onto the preview grid error maps.
     * @param {Array<{ row: number, fields: Record<string, string> }>} rowErrors
     * @param {'bulkEdit'|'checklist'} target
     */
    applyBulkValidateRowErrors(rowErrors = [], target = 'bulkEdit') {
      if (target === 'bulkEdit') {
        const { errors, hasErrors } = mergeCycleTaskRowErrors(
          this.dataerroreditBulk,
          rowErrors,
          'object',
        );
        if (!hasErrors) return;
        this.dataerroreditBulk = errors;
        this.bulkEditFileDataerror = true;
        this.flageditBulk = true;
        this.showdatawitherrorEditBulk = true;
        return;
      }
      const { errors, hasErrors } = mergeCycleTaskRowErrors(
        this.dataerror,
        rowErrors,
        'arrayOrObject',
      );
      if (!hasErrors) return;
      this.dataerror = errors;
      this.showdatawitherror = true;
      this.flag = true;
    },
    updateFilesBulk(newFilesBulk) {
      const nextFile = Array.isArray(newFilesBulk) ? newFilesBulk[0] : newFilesBulk;
      this.file = nextFile;
      this.readFile('file');
    },

    toggleSearch() {
      this.showSearch = !this.showSearch; // Toggle search box visibility
    },

    async loadTasklistData(force) {
      // Early exit if component is no longer active (user navigated away)
      if (!this.isComponentActive) {
        return;
      }
      const taskListObj = this.$store.getters['tasks/getTaskListData'] || {};
      const useCache = !force && taskListObj?.metadata?._id === this.id;
      if (!useCache) {
        this.loading = true;
      }
      // If taskListObj is valid and matches the current id, use cached data
      if (useCache) {
        // Resolve the layout first so headers are assigned once (no intermediate column set).
        let layoutData = null;
        let layoutLoaded = false;
        try {
          layoutData = await this.$store.dispatch('tasks/fetchCustomLayout', {
            pageName: this.page_name,
            forceRefresh: false,
          });
          layoutLoaded = true;
        } catch (error) {
          console.error("[Tasklist] Error fetching custom layout:", error);
        }
        this.applyCachedTasklistData(taskListObj, { layoutLoaded, layoutData });
        this.loading = false;
        this.$nextTick(() => this.syncTasklistViewAfterLoad());
        return;
      }
      try {
        if (!this.isComponentActive) {
          return;
        }
        const taskListData = await this.$store.dispatch('tasks/getAllTasks', {
          checklistId: this.id,
          forceRefresh: force,
          progressive: true,
        });
        
        if (!this.isComponentActive) {
          return;
        }
        this.metadata = taskListData.metadata || {};
        
        this.applyResolvedUserType(this.resolveLoadedUserType());
        this.cycleType = (taskListData.checklist?.[0]?.cycle_type) || "FBC";
        this.checklist = taskListData.checklist || [];
        this.coaCategoryAcc = taskListData.coaCategoryAcc || [];
        this.coaSubcategoryAcc = taskListData.coaSubcategoryAcc || [];
        this.categoryAcc = taskListData.categoryAcc || [...this.coaCategoryAcc];
        this.subcategoryAcc = taskListData.subcategoryAcc || [...this.coaSubcategoryAcc];
        const customColumnSchema = taskListData.customColumnSchema || [];
        this.customColumnSchema = customColumnSchema;
        const { localCustomColumns, taskMasterCustomColumns, tableHeadersfilter } = handleCustomColumnHeadersUtil(taskListData.customColumns, this.tableHeadersfilter, customColumnSchema);
        this.localCustomColumns = localCustomColumns;
        this.taskMasterCustomColumns = taskMasterCustomColumns;
        this.tableHeadersfilter = tableHeadersfilter;
        await this.getCustomLayout(force);
        this.page_details = { cycle_id: this.id, pagename: this.page_name, layout: this.tableHeadersfilter };

        this.tasklistDataCopy = Array.isArray(taskListData.checklist) ? [...taskListData.checklist] : [];
        this.$store.commit('tasks/SET_MANAGER_SUMMARY', taskListData.summary?.manager || {});
        this.$store.commit('tasks/SET_PREPARER_SUMMARY', taskListData.summary?.preparer || {});
        this.$store.commit('tasks/SET_APPROVER_SUMMARY', taskListData.summary?.approver || {});
        this.$store.commit('tasks/SET_LOCATION_LIST', taskListData.locationList || []);
        this.$store.commit('tasks/SET_USERS_LIST', taskListData.usersList || []);
      } catch (err) {
        this.$store.commit("sbError", err.message || err || "Unknown error!");
        console.error(err);
      } finally {
        if (this.isComponentActive) {
          // v3: statusDetails and __computed fields are already set by getAllTasks — no calculateDetails needed
          this.loading = false;
        }
        this.$nextTick(() => this.syncTasklistViewAfterLoad());
      }
    },
    /**
     * Fetches distinct non-AR category/subcategory values across all cycles
     * and merges them with COA lists into categoryAcc/subcategoryAcc.
     */
    async fetchAndMergeCrossCycleCategories() {
      const { categoryAcc, subcategoryAcc } = await this.$store.dispatch('tasks/fetchMasterCategories', {
        cycleType: this.cycleType,
        level: 'cycle',
        coaCategoryAcc: this.coaCategoryAcc,
        coaSubcategoryAcc: this.coaSubcategoryAcc,
      });
      this.categoryAcc = categoryAcc;
      this.subcategoryAcc = subcategoryAcc;
    },
    // To get custom columns by cycle type
    // This function fetches custom columns based on the cycle type and formats them
    async getCustomColumnsByCycleType() {
      try {
        const customColumnsResponse = await this.axios.post('/v2/financialclose/masters/checklist/readColumn/',{query:{cycle_type:this.cycleType.toUpperCase(), deleted: false}});
          if (customColumnsResponse?.data?.status === 'success' && Array.isArray(customColumnsResponse?.data?.data) && customColumnsResponse?.data?.data.length > 0) {
           return formatCustomColumns(customColumnsResponse?.data?.data);
          }else{
           return []
          }
      } catch (err) {
        this.$store.commit("sbError", err?.message || err || "Failed to get custom columns by cycle type");
        console.error("Error fetching custom columns by cycle type:", err);}
      return [];  
      },

    // calculateDetails — no longer needed with v3 (server-side computed __computed fields)
    // Kept as no-op for backward compatibility with any child component calls
    calculateDetails() {
      // v3: threshold, locationName, tbtotals, alltaskassigned are now in __computed per task
    },
    async getCustomLayout(force = false) {
      this.page_details = { cycle_id: this.id, pagename: this.page_name };
      this.custom_layout_success = false;

      try {
        // Fetch layout via Vuex action (caching is handled in the store)
        const layoutData = await this.$store.dispatch('tasks/fetchCustomLayout', {
          pageName: this.page_name,
          forceRefresh: force
        });
        this.custom_layout_success = true;
        this.seedPendingFilterFromLayout(layoutData);
        this.applyCustomLayout(layoutData);
      } catch (error) {
        console.error("[Tasklist] Error fetching custom layout:", error);
      }
    },
    /**
     * Restore saved column filters from getcustomlayout (keyed by cycle id).
     * Manage/Tasklist does the same; without this, Save Filter is lost on page reload.
     * Prefer an existing pending filter from listViewState (back-nav / in-session).
     */
    seedPendingFilterFromLayout(layoutData) {
      const scopedFilter = layoutData?.filter?.[this.id];
      const hasScopedFilter =
        scopedFilter &&
        typeof scopedFilter === 'object' &&
        Object.keys(scopedFilter).length > 0;
      if (!hasScopedFilter) return;
      const pendingEmpty =
        !this.pendingTableFilter ||
        Object.keys(this.pendingTableFilter).length === 0;
      if (!pendingEmpty) return;
      const filterCopy = JSON.parse(JSON.stringify(scopedFilter));
      this.pendingTableFilter = filterCopy;
      // Seed Vuex before TasklistView mounts so selectfilter is non-empty
      // and empty filter-item remount races do not wipe the saved filter.
      this.$store.dispatch('tasks/saveListViewState', {
        projectId: this.id,
        partial: { tableFilterHeader: filterCopy },
      });
    },
    /**
     * Build the final column set from a layout row (sync) and assign it only when it
     * differs from the current headers. Saved column visibility is kept. Role defaults
     * fill Tags / No. of Evidences only when the layout has no visibility for them.
     * A persona change still updates those columns through filterListByUser.
     * @param {Object|null} layoutData - getcustomlayout row, or null when none is saved
     * @param {Object} [tableFilterHeader] - filter for recon columns; defaults to the mounted table's
     */
    applyCustomLayout(layoutData, tableFilterHeader) {
      const { headers, defaultCloneHeaders, defaultHeaders } = buildTasklistHeadersFromLayout({
        layoutData,
        defaultHeaders: this.defaultTableHeadersfilter,
        customColumns: this.customColumns,
      });
      this.defaultCloneTableHeadersfilter = defaultCloneHeaders;
      this.defaultTableHeadersfilter = defaultHeaders;
      if (this.userType) {
        const savedLayout = layoutData?.custom_layout?.length > 0
          ? layoutData.custom_layout
          : null;
        applyUserTypeHeaderVisibility(headers, this.userType, savedLayout);
      }
      this.setTableHeaders(this.resolveTasklistHeaderVisibility(headers, tableFilterHeader));
    },
    // refresh data — loads all Tasklist APIs into Vuex on first load; toggles hydrate from store cache
    async refreshData(force = true) {
      if (!this.isComponentActive) {
        return;
      }
      this.id = this.$route.params.id || "";
      this.userid = this.$store.state.user.id;
      this.clientId = this.$store.state.client;
      if (!this.id) {
        console.warn('[TasksLandingPage]: Route param id is missing. API call skipped.');
        this.loading = false;
        return;
      }

      if (force) {
        this.loading = true;
      }

      try {
        await this.$store.dispatch('tasks/ensureTasklistPageData', {
          checklistId: this.id,
          pageName: this.page_name,
          forceRefresh: force,
          progressive: true,
        });
        // Hydrate local state from Vuex — APIs already loaded above (no duplicate network calls).
        await this.loadTasklistData(false);
        await this.fetchFormDataV3(false);
      } catch (err) {
        console.error('[Tasklist] refreshData error:', err);
      } finally {
        if (this.isComponentActive) {
          this.loading = false;
        }
      }
      this.syncHorizonAfterDataLoad();
    },
    async fetchFormDataV3(force = false) {
      if (!this.isComponentActive) return;
      try {
        const formData = await this.$store.dispatch('tasks/fetchFormDataV3', { forceRefresh: force });
        if (!this.isComponentActive) return;
        if (formData) {
          this.setActiveUserDetails((formData.users || []).filter(u => u.isActive));
        } else {
          // Keep store-backed users if local formData fetch returned null
          this.setActiveUserDetails(this.$store.getters['tasks/getActiveUsersList'] || []);
        }
        this.enforceHorizonViewAccess();
      } catch (error) {
        console.error('[Tasklist] fetchFormDataV3 error:', error);
        this.setActiveUserDetails(this.$store.getters['tasks/getActiveUsersList'] || []);
      }
    },
    onEditTaskFromRow(task) {
      if (!task?._id) return;
      if (!(this.$nova.hasRight(this.right, 'edit') || this.$nova.hasRight(this.right, 'add'))) {
        this.$store.commit('sbError', 'You do not have permission to edit this task.');
        return;
      }
      this.$refs.tasklistPageHeader?.openTaskForEdit(task);
    },
    viewTaskDetails(item) {
      this.viewtaskdata = { ...item };
      this.populatetblinkdialogitems(item, false);
      const counts = item.infoManagerRequestCounts
        || item.__computed?.infoManagerRequestCounts
        || { total: 0, completed: 0 };
      const infoTotal = Number(counts.total) || 0;
      const infoCompleted = Number(counts.completed) || 0;
      this.inforeqstage = infoTotal > 0 ? (infoCompleted / infoTotal) * 100 : 0;
      this.OpenBulkUploadBox(item);

      this.saveListViewState();

      const listView = this.$refs.tasklistView;
      const navSnapshot = buildDetailNavigationSnapshot({
        filteredTasks: listView?.filterbysearch || [],
        taskId: item._id,
        page: listView?.page,
        itemsPerPage: listView?.itemsPerPage,
      });
      this.$store.dispatch('tasks/saveListViewState', {
        projectId: this.id,
        partial: navSnapshot,
      });

      openTaskDetailPage({
        router: this.$router,
        store: this.$store,
        task: item,
        checklistId: this.id,
        userType: this.userType,
        tasklistView: this.isHorizonView ? HORIZON_VIEWS.HORIZON : HORIZON_VIEWS.LIST,
      });
    },

    // download ppt for all closed task
    downloadPPT() {
      if (!this.alltaskassigned)
        this.$store.commit(
          "sbError",
          "Assign all tasks before generating the PPT"
        );
      else if (this.alltaskopen)
        this.$store.commit("sbError", "No task closed to generate the PPT");
      else
        this.$nova.downloadFile(
          "",
          {},
          "/v2/financialclose/analysis/downloadppt/" + this.metadata._id
        );
    },
    // download excel
    downloadExcel() {
      if (!this.tasklistFullyLoaded) return;
      // Build header row from tableHeadersfilter, exclude columns with type: 'action'
      const headers = this.tableHeadersfilter
        .filter(h => h.visible !== false && h.type !== "action")
        .map(h => ({
          text: h.text || h.label || h.displayText || h.value,
          value: h.value
        }));

      // Add Linked GLs column after __tbtotals if not already present
      const tbtotalsIndex = headers.findIndex(h => h.value === "__tbtotals");
      const hasLinkedGLs = headers.some(h => h.value === "linkedgl");
      if (tbtotalsIndex !== -1 && !hasLinkedGLs) {
        headers.splice(tbtotalsIndex + 1, 0, { text: "Linked GLs", value: "linkedgl" });
      }

      // Ensure NXTGEN-6843 fields are always present in export (even if hidden in column config)
      [
        { text: EVIDENCE_REQUIRED.EXCEL_HEADER, value: "evidence_required" },
        { text: AUTO_APPROVE.EXCEL_HEADER, value: "auto_approve" },
      ].forEach((col) => {
        if (!headers.some((h) => h.value === col.value)) {
          headers.push(col);
        }
      });

      // Prepare the data rows with special logic for calculated columns
      const processedTaskList = this.$refs.tasklistView?.processedTaskList || [];
      const dataRows = processedTaskList.map(v => {
        return headers.map(h => {
          const yesNoCell = formatTasklistYesNoExportCell(h.value, v);
          if (yesNoCell) return yesNoCell;

          switch (h.value) {
            case "__tbtotals": // TB Amount
              return {
                v: ((v.__tbtotals || {}).total || 0).toFixed(0),
                z: "#,##",
                t: "n",
              };
            case "linkedgl": // Linked GLs - extract GL codes from __tbtotals
              return this.getLinkedGLsForExport(v);
            case "scheduleAmount": // Recon Amount
              return {
                v: calculateScheduleAmount(v),
                z: "#,##",
                t: "n",
              };
            case "explainedDifference": // Explained Difference
              return {
                v: calculateExplainedDifference(v),
                z: "#,##",
                t: "n",
              };
            case "difference": // Unexplained Difference
              return {
                v: calculateUnexplainedDifference(v),
                z: "#,##",
                t: "n",
              };
            case "duedate1": // Due date formatting
              return v.duedate ? this.$nova.formatDate(v.duedate) : "";
            case "locationName":
              return this.getLocationById(v.location);
            case "preparerids":
              return Array.isArray(v.preparerids) ? v.preparerids.join(", ") : (v.preparerids || "");
            case "approverids":
              return Array.isArray(v.approverids) ? v.approverids.join(", ") : (v.approverids || "");
            case "stage1": // Status column
              {
                const chip = this.getStageText(v.stage);
                return chip ? chip.text : "";
              }
            case "tags": // Tags - array handling
              return Array.isArray(v.tags) ? v.tags.join(", ") : (v.tags || "");
            case "assertions":
              return Array.isArray(v.assertions) ? v.assertions.join(", ") : (v.assertions || "");
            case "created_at": // Created on - date formatting
              return v.created_at ? this.$nova.formatDate(v.created_at) : "";
            case "submited_on": // Submission date - date formatting
              return v.submited_on ? this.$nova.formatDate(v.submited_on) : "";
            case "closed_date": // Completion date - date formatting
              return v.closed_date ? this.$nova.formatDate(v.closed_date) : "";
            case "statusdetail": // Status detail - text representation
              {
                const statusConfig = this.getStatusConfig(v.statusdetail);
                return statusConfig ? statusConfig.text : (v.statusdetail || "");
              }
            case "no_of_evidence": // No. of evidences
              return v.no_of_evidence !== undefined ? v.no_of_evidence : 0;
            case "inforrequest": // Information request
              return v.inforrequest || "";
            case "task_type": // Task type
              return v.task_type || "";
            default: {
              const raw = v[h.value];
              return raw !== undefined ? raw : "";
            }
          }
        });
      });

      // Title section rows
      const titleRows = [
        [
          { v: "Tasklist Status Tracker", s: { font: { bold: true, sz: 20 } } },
        ],
        [
          { v: this.metadata.description, s: { font: { bold: true, sz: 14 } } },
        ],
        [
          {
            v:
              this.$nova.formatDate(this.metadata.from) +
              " - " +
              this.$nova.formatDate(this.metadata.to),
            s: { font: { bold: true, sz: 14 } },
          },
        ],
        [undefined],
      ];

      // Prepare the final array for Excel (title + header + data)
      const excelData = [
        ...titleRows,
        headers.map(h => ({ v: h.text, s: { font: { bold: true } } })),
        ...dataRows
      ];

      // Create and export the Excel file
      let workbook = styledxlsx.utils.book_new();
      let worksheet = styledxlsx.utils.aoa_to_sheet(excelData);
      styledxlsx.utils.book_append_sheet(workbook, worksheet, "Tasklist");
      styledxlsx.writeFile(workbook, (this.metadata.description || "Tasklist") + ".xlsx");
    },
    workpaperTemplate() {
      this.$nova.downloadFile(
        "",
        {},
        "/v2/financialclose/analysis/gettasklisttemplateV3" // TODO - update the url
      );
    },
    getEmailByIdsBulk(ids) {
      if (!Array.isArray(ids)) return "";
      return ids
        .map((id) => this.userMap[id]?.email || "")
        .filter(Boolean)
        .join(", ");
    },
    getNameByIds(ids) {
      for (const i of ids) {
        return this.userMap[i]?.name || "";
      }
    },

    getUserList(x) {
      if (!Array.isArray(x)) return "";
      return x
        .map(id => this.userMap[id]?.name || "")
        .filter(Boolean)
        .join(", ");
    },

    getStageText(x) {
      if (x === 0) return { text: "Pending Submission", color: "", class: "pending-sub-chip" };
      else if (x === 1) return { text: "Processing", color: "", class: "pending-approval-chip" };
      else if (x === 2) return { text: "Pending Approval", color: "", class: "pending-approval-chip" };
      else if (x === 3) return { text: "Approved", color: "", class: "closed-task-chip" };
      else if (x === -1) return { text: "Rejected", color: "lightred", class: "error--text cancelled-chip" };
      else return { text: "Unknown", color: "grey", class: "" };
    },
    getStatusText(x) {
      if (x === "pending_not_due") return "Pending Submission Not Due";
      else if (x === "pending_over_due") return "Pending Submission Overdue";
      else if (x === "pending_due") return "Pending Submission Due";
      else if (x === "pendingapproval_due") return "Pending Approval Due";
      else if (x === "pendingapproval_not_due")
        return "Pending Approval Not Due";
      else if (x === "pendingapproval_over_due")
        return "Pending Approval Overdue";
      else return "approved";
    },
    // delete any task in tasklist
    async deleteChecklistItem(item) {
      if (!item?._id) return;
      this.loading = true;
      try {
        const result = await this.$store.dispatch('tasks/deleteChecklistItem', {
          checklistId: this.id,
          taskId: item._id,
        });
        if (result?.success) {
          this.$store.commit('sbSuccess', 'Tasklist item deleted');
          if (result.refreshError) {
            this.$store.commit('sbError', result.refreshError);
          }
          await this.refreshData(false);
        } else {
          this.$store.commit('sbError', result?.errorMessage || 'Unknown error!');
        }
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.error('[TasksLandingPage]: deleteChecklistItem error', err);
      } finally {
        this.loading = false;
      }
    },
    async unlinkBot(v) {
      this.loading = true;
      try {
        const result = await this.$store.dispatch('tasks/unlinkBotTasks', {
          checklistId: this.id,
          taskIds: [v._id],
        });
        if (result.success) {
          this.$store.commit("sbSuccess", this.$constants.BOT_NAME + " Unlinked");
          this.refreshData();
        }
      } finally {
        this.loading = false;
      }
    },
    async bulkunlinkBot(v) {
      this.loading = true;
      const taskIds = (Array.isArray(v) ? v : []).map((item) => item._id || item.task_id).filter(Boolean);
      try {
        const result = await this.$store.dispatch('tasks/unlinkBotTasks', {
          checklistId: this.id,
          taskIds,
        });
        if (result.success) {
          this.$store.commit("sbSuccess", this.$constants.BOT_NAME + " Unlinked");
          this.calculateDetails();
          this.refreshData();
        }
      } finally {
        this.loading = false;
      }
    },
    // tb amount amount calculation for table
    populatetblinkdialogitems(data = {}, opendialog = true) {
      this.tblinkdialogitems = [];
      this.tblinkdialogitems = (data.__tbtotals || {})?.items || [];
      this.tblinkdialogchecklistdata = data;
      if (opendialog) this.tblinkdialog = true;
    },

    // On open bulkupload add filelist
    async OpenBulkUploadBox(dt) {
      if (dt.upload) {
        if ((dt.stage === 2 || dt.stage === 3) && dt.upload) {
          await this.axios
            .post("/v2/upload/filenames/" + dt?.upload)
            .then((dt) => {
              this.$refs.tasklistView?.setFileNamesList(dt.data.data);
            })
            .catch((err) => {
              this.$store.commit(
                "sbError",
                err.message || err || "Unknown error!"
              );
              console.log(err);
            });
        }
      } else {
        this.$refs.tasklistView?.setFileNamesList([]);
      }
    },
    // change user type
    overDueDateFun() {
      this.overDueDate = !this.overDueDate;
      this.saveListViewState({ overDueDate: this.overDueDate });
    },
    redirectChatBot(){
      // Context (projectId / taskId) is synced from the route inside toggleChatbot
      this.$root.toggleChatbot?.();
    },
    filterListByUser(user) {
      this.userType = user;
      this.$store.commit('setTasklistUserType', user);
      this.syncUserTypeQueryParam(user);
      this.syncHorizonViewUserType(user);
      const checklistId = this.$route.params.id || this.id;
      persistTasklistUserType(checklistId, user);
      this.$refs.tasklistView?.clearSelection();

      applyUserTypeHeaderVisibility(this.tableHeadersfilter, user);
      this.saveListViewState({ userType: user });
    },
    // add header
    // addTableHeader(item) {
    //     this.tableHeaders.find((x) => x === item ? x.visible = !x.visible : "")
    // },
    addTableHeader(item) {
      // this.tableHeadersfilter.find((x) =>
      //   x === item ? (x.visible = !x.visible) : ""
      // );
      this.tableHeadersfilter.find((x) =>
        x === item ? (x.visible = !x.visible) : ""
      );
      
    },
    getLocationById(id) {
      let locationname = this.locationList?.find(
        (location) => location._id === id
      );
      return locationname?.name || "";
    },
    getNameById(id) {
      return this.userMap[id]?.name || "";
    },

    filterWithTask(x) {
      this.selectedTabStage = x;
      this.saveListViewState({ selectedTabStage: x });
    },
    customStageOrder(arr) {
      const stageOrder = [2, 0, 3];
      return arr.sort((a, b) => {
        const aIndex = stageOrder.indexOf(a.stage);
        const bIndex = stageOrder.indexOf(b.stage);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    },
    bulklocationActions() {
      this.$refs.bulkmenu.isActive = false;
      this.bulkactions = 0;
    },
    getConversation(task) {
      this.axios
        .post(
          "/v2/financialclose/analysis/getconversation/" +
            this.metadata._id +
            "/c/" +
            task._id
        )
        .then((dt) => {
          if (dt.data.status === "success") {
            this.conversationList = dt.data.data;
            // if (mark) this.markAsRead();
          } else throw Error(dt.data.message || "Unknown error");
        })
        .catch((err) => {
          this.$store.commit("sbError", err.message || err || "Unknown error!");
          console.log(err);
        })
        .finally(() => {
          // this.scrollToBottom();

          const chatContainer = this.$refs.chatContainer;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer?.scrollHeight;
          }
        });
    },

    sendQuery(task) {
      if (this.queryMsg) {
        // this.loading = true;
        let dataObject = {
          type: "string",
          value: this.queryMsg,
          status: "",
        };
        let successflag = false;
        this.axios
          .post(
            "/v2/financialclose/analysis/addconversation/" +
              this.metadata._id +
              "/c/" +
              task._id,
            { data: dataObject }
          )
          .then((dt) => {
            if (dt.data.status === "success") {
              successflag = true;
              // this.$store.commit("sbSuccess", "successfully sended");
              this.queryMsg = "";
            } else throw Error(dt.data.message || "Unknown error");
          })
          .catch((err) => {
            this.$store.commit(
              "sbError",
              err.message || err || "Unknown error!"
            );
            console.log(err);
          })
          .finally(() => {
            // this.loading = false;
            if (successflag) this.getConversation(task);
          });
      }
    },
    clearfileBulk(){
      this.resetbulkEditFile = !this.resetbulkEditFile;
      this.bulkEditFileDataList = [];
      this.bulkEditFileDataerror = false;
      this.flageditBulk = false;
      this.dataerroreditBulk = [];
      this.errorsonlyEditBulk = [];
      this.bulkEditErrorResponse = [];
      this.showdatawitherrorEditBulk = false;
    },
    clearfile() {
      this.resetFlag = !this.resetFlag;
      this.file = [];
      this.fileuplaoded = [];
      (this.uploaddata = []),
        (this.showdatawitherror = false),
        (this.dataerror = []),
        (this.flag = false),
        (this.file = null);
        this.checkListErrorResponse = '';
    },

    FilesUpload(filesss) {
      // Only store files, always assign a new array reference for reactivity
      this.fileuplaoded = Array.isArray(filesss) ? [...filesss] : [];
    },

    async uploadFiles(id) {
      let files = this.fileuplaoded;
      this.sendforreviewloading = true;
      // Validate files before upload
      if (Array.isArray(files) && files.length > 0) {
        // Only allow files that start with 'reco_'
        const validFiles = files.filter((file) => file.name.startsWith("reco_"));
        if (validFiles.length === 0) {
          this.$store.commit("sbError", "No valid files selected. File name must start with 'reco_'.");
          this.sendforreviewloading = false;
          return;
        }
        // Validate headers for each file
        const fileValidationErrors = [];
        for (const file of validFiles) {
          let ext = ((file || {}).name || "").split(".").pop();
          if (["xlsx", "xls", "xlsm", "xlsb"].indexOf(ext) === -1) {
            fileValidationErrors.push(`File '${file.name}' has unsupported extension. Only xlsx, xls, xlsm, xlsb are allowed.`);
            continue;
          }
          let dt = await this.$nova.readExcelFile(file, "TOP SHEET");
          let headings = [
            "Task No ",
            "Task description",
            "Location",
            "Particulars",
            "Amount",
            "File Reference",
            "Type"
          ];
          let headingsConsolidated = [
            "Task No",
            "Task description (Optional)",
            "Location",
            "Particulars",
            "Amount",
            "File Reference (Optional)",
            "Type",
            "Standalone Entity (refer # 11 in Instructions)",
            "GL (refer # 12 in Instructions)"
          ];
          if (
            JSON.stringify(dt[0] || []) !== JSON.stringify(headings) &&
            JSON.stringify(dt[0] || []) !== JSON.stringify(headingsConsolidated)
          ) {
            fileValidationErrors.push(`File '${file.name}' is missing required columns or they are not in the correct order.`);
          }
        }
        if (fileValidationErrors.length > 0) {
          this.$store.commit("sbError", fileValidationErrors.join(' '));
          this.sendforreviewloading = false;
          return;
        }
        // If all files are valid, proceed to upload
        let arrayPush = [];
        const uploadPromises = validFiles.map(async (file) => {
          try {
            let dt = await this.$nova.uploadFile(this.axios, file, {});
            arrayPush.push(dt);
          } catch (err) {
            this.fileprocessing[id] = 0;
            this.$store.commit("sbError", err.message || err || "Unknown error!");
            console.error(err);
          }
        });
        await Promise.all(uploadPromises);
        if (arrayPush.some((response) => response.data.status === "success")) {
          this.dataArray = arrayPush.flatMap((response) =>
            response.data.data.map((item) => {
              return {
                filename: item.uploadname,
                file_id: item._id,
                file_path: item.path,
              };
            })
          );
          // this.fileprocessing[id] = 2;

          // Rest of your code...
          let data = {
            upload: this.dataArray,
            project: this.id,
            checklist: this.viewtaskdata,
            bulkupload: false,
          };
          // Continue with the post request
          try {
            let response = await this.axios.post(
              "/v2/financialclose/analysis/storeworkpaper",
              {
                data: [data],
              }
            );

            if (response?.data?.status === "success") {
              this.$store.commit("sbSuccess", "File uploaded");
              this.refreshData();
              // this.fileprocessing[id] = 0;
              // this.sendforreviewdata.stage = 1;
              // this.checklist[this.sendforreviewdata.__key] = {
              //     ...this.sendforreviewdata
              // };
            } else {
              throw new Error(
                response?.data?.message || "Error uploading file"
              );
            }
          } catch (axiosError) {
            // this.fileprocessing[id] = 0;
            this.$store.commit(
              "sbError",
              axiosError?.response?.data?.message || axiosError?.message || "Unknown error!"
            );
            console.error(axiosError);
          } finally {
            // Reset file and loading states
            this.files = {};
            // this.refreshData();
            this.sendforreviewloading = false;
          }
        } else {
          // this.fileprocessing[id] = 0;
          this.$store.commit("sbError", "Unable to upload file");
          // this.sendforreviewloading = false
        }
      } else {
        this.sendforreviewloading = false;
        this.$store.commit("sbError", "No files to upload");
        this.sendforreviewloading = false;
      }
    },

    async uploadFilesBulk() {
      let files = this.fileuplaoded;
      this.sendforreviewloadingbulk = true;
      // let taskIds=  this.selectedTasks.length>0 ? this.selectedTasks : [this.viewtaskdata];
      this.log = 0;
      try {
        let totalSize = 0;
        let dataArrayList = [];
        if (Array.isArray(files) && files.length > 0) {
          files.forEach((file) => {
            if (file.name) {
              totalSize += file.size;
            }
          });
          if (totalSize > 50 * 1024 * 1024) {
            this.$store.commit(
              "sbError",
              "Total file size exceeds the limit (50 MB)"
            );
            return;
          }
          this.sendforreviewloadingbulk = true;
          const uploadPromises = files.map(async (file) => {
            if (file.name) {
              try {
                let response = await this.$nova.uploadFile(
                  this.axios,
                  file,
                  {}
                );
                dataArrayList.push(response);
              } catch (uploadError) {
                this.$store.commit(
                  "sbError",
                  uploadError.message || "Error uploading file"
                );
                console.error(uploadError);
              }
            }
          });
          this.bulkfilesloading = 0;
          await Promise.all(uploadPromises);
          if (
            dataArrayList.some((response) => response.data.status === "success")
          ) {
            this.dataArray = dataArrayList.flatMap((item) =>
              item.data.data.flatMap((item) => {
                return {
                  filename: item.uploadname,
                  file_id: item._id,
                  file_path: item.path,
                };
              })
            );
            let data = {
              bulkupload: true,
              upload: this.dataArray,
              project: this.id,
              checklist:
                this.selectedTasks.length > 0
                  ? this.selectedTasks
                  : [this.viewtaskdata],
              removelocalfile: false,
            };
            try {
              this.bulkfilesloading = 0;
              let response = await this.axios.post(
                "/v2/financialclose/analysis/bulkStoreWorkPaper",
                { data: [data] }
              );
              if (response?.data?.status === "success") {
                this.$store.commit("sbSuccess", "File uploaded");
                this.$refs.tasklistView?.closeBulkWorkpaperUploadDialog();
                this.bulkfilesloading = 1;
                // if (index === (this.selectedTasks.length - 1)) {
                // }
                this.sendForReviewDialogbulk = false;
                this.refreshData();
              } else {
                throw new Error(
                  response?.data?.message || "Error Uploading File"
                );
              }
            } catch (axiosError) {
              this.filesbulk = {};
              this.clearuploadfiledata = true;
              this.$store.commit(
                "sbError",
                axiosError?.response?.data?.message || axiosError?.message || "Unknown error!"
              );
            }

            this.sendforreviewloadingbulk = false;
          } else {
            this.$store.commit("sbError", "Unable to upload file ");
            this.sendforreviewloadingbulk = false;
          }
        } else {
          this.$store.commit("sbError", "No files to upload");
          this.sendforreviewloadingbulk = false;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.sendforreviewloadingbulk = false;
        this.filesbulk = {};
        this.bulkfilesloading = 1;
        this.clearuploadfiledata = true;
        this.sendforreviewdialogbulk = false;
        this.refreshData();
      }
      this.resetFlag = !this.resetFlag;
    },

    bulkopenlinkBotDialog(taskMap) {
      this.$refs.tasklistView?.openBulkLinkBotDialog(taskMap);
    },

    gettaskdetails(id) {
      return this.tasklistData.find((item) => item._id === id._id);
    },
    bulkManageUser(action, usertype, userid, selectitems, changeuserid) {
      let ndt = {};
      if (usertype === "Location") {
        if (userid === "") {
          throw new Error("Please select location");
        }
      }
      if (action === "replace") {
        ndt = { fromuserid: userid, touserid: changeuserid, type: action };
      } else {
        ndt = {
          userid: userid,
          usertype: (usertype || "").toLowerCase(),
          type: action,
        };
      }
      let fdt = { type: "checklist", _id: selectitems.map((item) => item._id) };

      let successflag = false;
      this.axios
        .post(
          "/v2/financialclose/analysis/bulkmanageuser/" + this.metadata._id,
          { data: [ndt], filter: fdt }
        )
        .then((dt) => {
          if (dt.data.status === "success") {
            this.$refs.tasklistView?.closeBulkActionUserDialog();
            let successMessage = "";
            if (action === "associate") successMessage = "User Added";
            else if (action === "remove") successMessage = "User Removed";
            else if (action === "move")
              successMessage = "Change to " + usertype;
            else if (action === "replace") successMessage = "User Replaced";
            this.$store.commit("sbSuccess", successMessage);
            successflag = true;
            this.selectedTasks = [];
          } else {
            throw new Error(dt.data.message || "Error changing status");
          }
        })
        .catch((err) => {
          this.$store.commit("sbError", err.message || err || "Unknown error!");
        })
        .finally(() => {
          this.bulkactionuserloading = false;
          if (successflag) this.refreshData();
        });
    },
    /**
     * Bulk download files from selected tasks
     * Uses extractFileIdsFromTask utility for backward compatibility with upload and upload_v2
     * @param {Array} items - Array of selected task items
     */
    downloadbbulk(items) {
      if (!Array.isArray(items) || items.length === 0) {
        this.$store.commit('sbError', 'No tasks selected for download');
        return;
      }

      // Collect file IDs from all selected tasks using utility function
      const fileIds = items.flatMap(task => extractFileIdsFromTask(task));
      
      // Remove duplicates
      const uniqueFileIds = [...new Set(fileIds)];

      if (uniqueFileIds.length === 0) {
        this.$store.commit('sbError', 'No files available to download');
        return;
      }

      this.$nova.downloadFiles(uniqueFileIds);
    },
    /**
     * Download files from a single task
     * Uses extractFileIdsFromTask utility for backward compatibility
     * @param {Object} task - Task object
     */
    downloadSingleTaskFiles(task) {
      const fileIds = extractFileIdsFromTask(task);
      
      if (fileIds.length === 0) {
        this.$store.commit('sbError', 'No files available to download');
        return;
      }

      this.$nova.downloadFiles(fileIds);
    },
    changeChecklistStatus(analysis, type, remark) {
      let idtxt = analysis;
      const trimmedRemark = typeof remark === 'string' ? remark.trim() : '';
      if (
        (type === CHECKLIST_STATUS_ACTION.REJECT || type === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING)
        && !isValidRejectionRemark(trimmedRemark)
      ) {
        this.$store.commit(
          'sbError',
          trimmedRemark ? REJECTION_REMARK.INVALID : REJECTION_REMARK.REQUIRED
        );
        return;
      }
      const data = { type };
      if (
        trimmedRemark
        || type === CHECKLIST_STATUS_ACTION.REJECT
        || type === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING
        || type === CHECKLIST_STATUS_ACTION.APPROVE
        || type === CHECKLIST_STATUS_ACTION.SIGNOFF
      ) {
        Object.assign(data, buildChecklistStatusRemarkPayload(type, trimmedRemark));
      }

      if (Array.isArray(analysis)) {
        const eligibleTasks = analysis.filter((item) => !this.isClosedTask(item));
        const skippedCount = analysis.length - eligibleTasks.length;

        if (eligibleTasks.length === 0) {
          this.$store.commit('sbError', 'Approved tasks cannot be modified');
          return;
        }

        if (skippedCount > 0) {
          this.$store.commit(
            'sbWarning',
            `${skippedCount} approved task${skippedCount === 1 ? '' : 's'} excluded from this action`
          );
        }

        idtxt = 'bulk';
        data.checklist = eligibleTasks.map((item) => item._id);
      } else {
        const masterTaskList = this.$refs.tasklistView?.masterTaskList || [];
        const task = masterTaskList.find((t) => t._id === analysis);
        if (task && this.isClosedTask(task)) {
          this.$store.commit('sbError', 'Approved tasks cannot be modified');
          return;
        }
      }

      this.loading = true;
      this.axios
        .post(
          "/v2/financialclose/analysis/changecheckliststage/" +
            this.metadata._id +
            "/a/" +
            idtxt,
          { data: [data] }
        )
        .then((dt) => {
          if (dt.data.status === "success") {
            this.$store.commit("sbSuccess", "Status Updated");
            this.refreshData();
          } else throw dt.data.message || "Error changing status";
        })
        .catch((err) => {
          this.$store.commit("sbError", err.message || err || "Unknown error!");
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    sendForReview() {
      // this.sendforreviewloading = true;
      let url =
        "/v2/financialclose/analysis/sendforreview/" +
        this.id +
        "/c/" +
        this.viewtaskdata._id;
      this.axios
        .post(url, { data: { remark: this.fileremark } })
        .then((dt) => {
          if (dt.data.status === "success") {
            this.$store.commit("sbSuccess", "Tasklist sent for review");
            // this.sendforreviewdialog = false;
            this.files = [];
            this.filesbulk = [];
            this.viewtaskdata.stage = 2;
            this.checklist[this.viewtaskdata.__key] = { ...this.viewtaskdata };
            this.calculateDetails();
            this.refreshData();
          } else throw Error(dt.data.message || "Unknown error");
        })
        .catch((err) => {
          this.$store.commit("sbError", err.message || err || "Unknown error!");
          console.log(err);
        })
        .finally(() => {
          this.$refs.tasklistView?.closeBulkWorkpaperUploadDialog();
        });
    },
    sendForReviewBulk() {
      const tasklistid = this.selectedTasks.map((item) => item._id);
      let url = "v2/financialclose/analysis/bulksendforreview";
      this.axios
        .post(url, {
          data: { remark: this.fileremark, pid: this.id, cidList: tasklistid },
        })
        .then((dt) => {
          if (dt.data.status === "success") {
            this.$store.commit("sbSuccess", "Tasklist sent for review");
            this.$refs.tasklistView?.closeBulkWorkpaperUploadDialog();
            // this.sendforreviewdialog = false;
            this.files = [];
            this.filesbulk = [];
            this.selectedTasks = [];
            this.viewtaskdata.stage = 2;
            this.checklist[this.viewtaskdata.__key] = { ...this.viewtaskdata };
            // this.calculateDetails();
            this.refreshData();
          } else throw Error(dt.data.message || "Unknown error");
        })
        .catch((err) => {
          this.$store.commit("sbError", err.message || err || "Unknown error!");
          console.log(err);
        })
        .finally(() => {
          this.$refs.tasklistView?.closeBulkWorkpaperUploadDialog();
        });
    },
    getRefresh() {
      this.refreshData(true);
    },
  // MARKER: TASK_TYPE
  readBulkEditFile(files) {
  const bulkeditFile = files[0];
  if (!bulkeditFile || !bulkeditFile.name) return;
  this.showdatawitherrorEditBulk = false;
  const headings = [
    ...BULK_EDIT_FILE_HEADINGS,
    ...this.importCustomColumns.map((column) =>
      column.dataType === 'date'
        ? `${column.displayText} (dd/mm/yyyy)`
        : column.displayText
    ),
  ];
  this.bulkEditTableHeaders = [...this.$constants.TASK_LIST_CONSTANTS.BULK_EDIT_HEADERS_CLOSE_CYCLE];
  this.bulkEditTableHeaders.push({ text: 'Enforce Threshold', value: 'enforceThreshold' });
  this.bulkEditTableHeaders.push({ text: 'Auto-Approve', value: 'auto_approve' });
  this.bulkEditTableHeaders.push({ text: 'Evidence Required', value: 'evidence_required' });
  this.bulkEditTableHeaders.push(...this.importCustomColumns.map((column) => ({
    text: column.dataType === 'date' ? `${column.displayText} (dd/mm/yyyy)` : column.displayText,
    value: column.columnName,
  })));
  this.bulkEditDialogloading = true;
  const ext = bulkeditFile.name.split(".").pop();

  Promise.resolve()
    .then(() => {
      if (!["xlsx", "xls", "xlsm", "xlsb"].includes(ext)) {
        throw new Error("Only xlsx, xls, xlsm, xlsb file extensions supported");
      }
      return this.$nova.readExcelFile(bulkeditFile, "");
    })
    .then(async (dt) => {
      const sheetRows = Array.isArray(dt) ? dt : [];
      // Title row often has an empty first cell (e.g. cycle name). Find the real header by __taskId__.
      let headerOffset = 0;
      const headerRowIndex = sheetRows.findIndex((row) => {
        const cells = (row || []).map((c) => String(c ?? '').trim());
        if (cells[0] === '__taskId__') {
          headerOffset = 0;
          return true;
        }
        if (cells[0] === '' && cells[1] === '__taskId__') {
          headerOffset = 1;
          return true;
        }
        return false;
      });
      if (headerRowIndex < 0) {
        throw new Error(`Columns '${headings.join("','")}' are required in the same order`);
      }

      const fileHeaders = (sheetRows[headerRowIndex] || [])
        .slice(headerOffset)
        .map((c) => String(c ?? '').trim())
        .map((h) => canonicalizeBulkTaskHeader(h));

      // Require expected columns as an ordered prefix. Excel may include extra custom columns
      // from the download template even when local customColumns is empty.
      const headersMatch = headings.length <= fileHeaders.length
        && headings.every((h, i) => fileHeaders[i] === h);
      if (!headersMatch) {
        throw new Error(`Columns '${headings.join("','")}' are required in the same order`);
      }

      this.bulkEditFileDataerror = false;
      this.flageditBulk = false;
      const filedata = [];
      const errordata = [];
      for (let i = headerRowIndex + 1; i < sheetRows.length; i++) {
        const cleanedRow = (sheetRows[i] || []).slice(headerOffset);
        if (cleanedRow.length === 0 || cleanedRow.every((c) => c == null || String(c).trim() === '')) continue;
        const [
          __taskId__, ref, description, duedate, category, subcategory,
          purpose, instruction, priority, preparerName, approverName, signOffApproverName,
          tagss, linkedgls, locationName, assertionsCell, skipValidationValue, autoApproveValue, evidences, evidenceRequiredValue,
        ] = cleanedRow;
        const matchedTask = (this.$refs.tasklistView?.masterTaskList || []).find(
          x => {
            return x._id.toString() === __taskId__;
          }
        );
        if(!matchedTask) {
          continue;
        }
        
      const preparerNames = preparerName?.split(',').map(n => n.trim()).filter(Boolean) || [];
      const approverNames = approverName?.split(',').map(n => n.trim()).filter(Boolean) || [];
      const signOffApproverNames = signOffApproverName?.split(',').map(n => n.trim()).filter(Boolean) || [];
      const tags = tagss?.split(',').map(n => n.trim()).filter(Boolean) || [];
      const linkedgl = linkedgls?.toString().split(',').map(n => n.trim()).filter(Boolean) || [];
    
      const matchedPreparers = preparerNames.map(email => {
        return this.userEmailMap[email] || email;
      });
      const matchedApprovers = approverNames.map(email => {
        return this.userEmailMap[email] || email;
      });
      const matchedSignOffApprovers = signOffApproverNames.map(email => this.userEmailMap[email] || email);
      const matchedLocation = this.locationList.find(
        (l) => canonicalLocationKey(l.name) === canonicalLocationKey(locationName)
      );
      // Send spreadsheet GL codes; server resolves against cycle TB.
      const matchedGls = linkedgl;
      const matchedGlsIds = linkedgl;
      const assertions = String(assertionsCell ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      // Parse evidences explicitly. Preserve 0 and allow blank -> use existing value; invalid -> mark error later.
      let no_of_evidence;
      if (evidences === undefined || evidences === null || evidences === "") {
        // keep original or default 0
        no_of_evidence = (matchedTask.no_of_evidence === undefined || matchedTask.no_of_evidence === null) ? 0 : Number(matchedTask.no_of_evidence);
      } else if (!isNaN(Number(evidences)) && Number(evidences) >= 0) {
        no_of_evidence = Number(evidences);
      } else {
        no_of_evidence = NaN; // trigger validation error below
      }
      const parseYesNo = (value, fallback = false) => {
        if (value === undefined || value === null || value === '') return fallback;
        return ['yes', 'true', '1'].includes(String(value).trim().toLowerCase());
      };
      let evidence_required;
      if (evidenceRequiredValue === undefined || evidenceRequiredValue === null || evidenceRequiredValue === '') {
        evidence_required = coerceEvidenceRequired(matchedTask.evidence_required);
      } else {
        evidence_required = coerceEvidenceRequired(evidenceRequiredValue);
      }
      const duplicateUsers = preparerNames.filter(email => approverNames.includes(email));
      const duplicatePreparers = findDuplicateValues(preparerNames);
      const duplicateApprovers = findDuplicateValues(approverNames);
      const duplicateSignOffApprovers = findDuplicateValues(signOffApproverNames);
      let rowdata = {
        task_type: "",
        ref,
        description,
        duedate: duedate ? formatDateToDMY(duedate) : "",
        category,
        subcategory,
        purpose,
        instruction,
        priority,
        preparer: matchedPreparers,
        approver: matchedApprovers,
        sign_off_approver: matchedSignOffApprovers,
        sign_off_approvers: matchedSignOffApprovers,
        requires_sign_off: matchedSignOffApprovers.length > 0,
        tags,
        linkedgl:matchedGls,
        location: matchedLocation?._id || locationName,
        assertions,
        _id: __taskId__,
        glcode: matchedGlsIds,
        // Store actual evidences numeric value; also provide 'evidences' alias for table header mapping
        no_of_evidence,
        evidences: no_of_evidence,
        auto_approve: parseYesNo(autoApproveValue, false),
        evidence_required,
      };
      // TODO - Create BULK EDIt as separate component
      rowdata.task_type = rowdata.glcode.length ? this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION : this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
      rowdata.enforceThreshold = rowdata.task_type
        === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION
        ? parseYesNo(skipValidationValue, true)
        : false;
      const errors = {
        preparer:'',
        approver:'',
        sign_off_approver:'',
        linkedgl:'',
        evidences:''
      };
      let customfields = [];
      if (this.importCustomColumns.length > 0) {
          customfields = this.importCustomColumns.map((column) => {
              // Find header index accounting for date suffix: "Column Name (dd/mm/yyyy)"
              let headerIndex = -1;
              let value;
              const headerWithDateSuffix = `${column.displayText} (dd/mm/yyyy)`;
              if (column.dataType === 'date') {
                  headerIndex = fileHeaders.indexOf(headerWithDateSuffix);
                  value = headerIndex >= 0 ? formatDateToDMY(cleanedRow[headerIndex]) : '';
                   // If date value exists, validate and reformat it
              } else {
                  headerIndex = fileHeaders.indexOf(column.displayText);
                  value = headerIndex >= 0 ? cleanedRow[headerIndex] : '';
              }
             
              let fieldValue = value || ''; // Default field value

              // // Validate custom fields and add errors for mismatched data types
              // if (column.dataType === 'number' && isNaN(Number(value))) {
              //     errors[column.columnName] = `'${value}' is not a valid number`;
              // } else if (column.dataType === 'date' && isNaN(new Date(value).getTime())) {
              //     errors[column.columnName] = `'${value}' is not a valid date`;
              // }

              return { [column.columnName]: fieldValue };
          });
          rowdata = { ...rowdata, ...customfields.reduce((acc, field) => ({ ...acc, ...field }), {}) };
      }
      rowdata.customfields = customfields;
      if (!description) errors["description"] = "Description is required";
      if (!duedate) {
        errors["duedate"] = "Due date is required";
      } else {
        const dueDateWindow = getCycleDueDateWindow(this.metadata?.to, MAX_DUE_DATE_DAYS);
        const dueDateValidation = validateDueDateRange(duedate, MAX_DUE_DATE_DAYS, {
          minDate: dueDateWindow.minIso,
          maxDate: dueDateWindow.maxIso,
          usesCycleEnd: dueDateWindow.usesCycleEnd,
        });
        if (!dueDateValidation.valid) {
          errors["duedate"] = dueDateValidation.error;
        }
      }
      if (!category) errors["category"] = "Category is required";
      if (!subcategory) errors["subcategory"] = "Subcategory is required";
      if (!priority) errors["priority"] = "Priority is required";
      // Validate no_of_evidence: must be a number between 0 and 10
      if (no_of_evidence === null || no_of_evidence === undefined || isNaN(no_of_evidence)) {
        errors["evidences"] = "No. of Evidences must be a valid number";
      } else if (no_of_evidence < 0 || no_of_evidence > 10) {
        errors["evidences"] = "No. of Evidences must be between 0 and 10";
      }
      if (isEvidenceRequiredEnabled(evidence_required) && (no_of_evidence < 1 || no_of_evidence > 10)) {
        errors["evidences"] = "When Evidence Required is Yes, No. of Evidences must be between 1 and 10";
      }
      // if (!Array.isArray(matchedGls) || matchedGls.length === 0) errors["linkedgl"] = "Linked Gl is required";
      if (!preparerName) errors["preparer"] += " Preparer is required";
      if (!approverName) errors["approver"] += " Approver is required";
      if (duplicateUsers.length > 0) {
        errors["preparer"] += ` Preparer and Approver cannot be the same: ${duplicateUsers.join(", ")}`;
      }
      if (duplicatePreparers.length > 0) {
        errors["preparer"] += ` ${duplicateInRoleMessage(duplicatePreparers, 'preparer')}`;
      }
      if (duplicateApprovers.length > 0) {
        errors["approver"] += ` ${duplicateInRoleMessage(duplicateApprovers, 'approver')}`;
      }
      if (signOffApproverNames.length > 0) {
        if (!approverNames.length) {
          errors.sign_off_approver = SIGN_OFF.ERRORS.ORPHAN_L2;
        }
        const preparerOverlap = signOffApproverNames.filter((email) => preparerNames.includes(email));
        if (preparerOverlap.length > 0) {
          errors.sign_off_approver = SIGN_OFF.ERRORS.PREPARER_SOD;
        }
        const approverOverlap = signOffApproverNames.filter((email) => approverNames.includes(email));
        if (approverOverlap.length > 0) {
          errors.sign_off_approver = SIGN_OFF.ERRORS.APPROVER_SOD;
        }
        if (duplicateSignOffApprovers.length > 0) {
          errors.sign_off_approver = `${errors.sign_off_approver || ''} ${duplicateInRoleMessage(duplicateSignOffApprovers, 'sign-off approver')}`.trim();
        }
      }

      if (matchedTask) {
        const validGlcodes = (matchedTask.__tbtotals?.items || []).map(item => item.glcode);
        if (validGlcodes.length > 0 && linkedgl.length === 0) {
          rowdata.task_type = this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
          errors["linkedgl"] = `Linked GL(s) are mandatory for Account Reconciliation task`;
        }
      }

      if (!String(locationName || '').trim()) {
        errors["location"] = CHECKLIST_VALIDATION_MESSAGES.LOCATION_REQUIRED;
      }
     const errorKeys = ERROR_KEYS_CYCLE_TASKLIST;
      
    const hasError = errorKeys.some(key => errors[key]) || Boolean(errors.sign_off_approver);
    if (hasError) {
        rowdata.error = Object.values(errors).filter(Boolean).join(" | ");
        this.bulkEditFileDataerror = true;
        this.flageditBulk = true;
      }

      filedata.push(rowdata);
      errordata.push(errors);
      }

      // Server validates GL/COA/location/access before revealing the preview table
      // (keeps the drop-zone spinner up — no flash of an unvalidated grid).
      let mergedErrors = errordata;
      let serverErrorLines = [];
      if (filedata.length && this.id) {
        const validation = await this.$store.dispatch('tasks/validateCycleTaskList', {
          checklistId: this.id,
          data: filedata,
          mode: 'bulkUpdate',
        });
        const parsed = parseCycleTaskValidateResult(validation);
        if (!parsed.valid) {
          const { errors, hasErrors } = mergeCycleTaskRowErrors(
            errordata,
            parsed.rowErrors,
            'object',
          );
          if (hasErrors) {
            mergedErrors = errors;
            this.bulkEditFileDataerror = true;
            this.flageditBulk = true;
          }
          serverErrorLines = formatBulkUpdateRowErrorMessages(parsed.rowErrors);
        }
      }

      this.bulkEditFileDataList = filedata;
      this.dataerroreditBulk = mergedErrors;
      this.bulkEditErrorResponse = serverErrorLines;
      this.showdatawitherrorEditBulk = false;
      this.errorsonlyEditBulk = this.showdatawitherrorEditBulk
        ? this.dataerroreditBulk.filter((item) =>
            Object.values(item).some((error) => error)
          )
        : this.dataerroreditBulk;
    })
    .catch((err) => {
      this.$store.commit("sbError", err.message || err || "Unknown error!");
    })
    .finally(() => {
      this.bulkEditFileDataFile = null;
      this.bulkEditDialogloading = false;
    });
  },

   clearBulkEditFile(){
    this.resetbulkEditFile = !this.resetbulkEditFile;
   },

    /**
     * Active users for bulk-edit validation.
     * Prefers local userDetails, then store active list / formDataV3 / usersList.
     * userDetails.activeUsers can be empty if fetchFormDataV3 didn't populate it.
     */
    resolveActiveUsersForBulkEdit() {
      if (this.userDetails?.activeUsers?.length) {
        return this.userDetails.activeUsers;
      }
      const storeActive = this.$store.getters['tasks/getActiveUsersList'] || [];
      if (storeActive.length) return storeActive;

      const formUsers = this.$store.getters['tasks/getFormDataV3']?.users || [];
      const formActive = formUsers.filter((u) => u && u.isActive);
      if (formActive.length) return formActive;

      // usersList from tasklist cache is already filtered to active users
      return this.usersList || [];
    },

  },
  mounted() {
    this.$nextTick(() => {
      this.viewModeToggleReady = true;
      // Cycle switch remounts via router-view :key=fullPath. Vue Router treats that as a
      // param update (beforeRouteUpdate on the old instance), not a fresh beforeRouteEnter.
      // The old instance is destroyed (isComponentActive=false), so its refresh is aborted.
      // Load here when the new instance still has no matching metadata / no in-flight load.
      if (!this.isComponentActive || this.tasklistLoadStarted) return;
      const checklistId = this.$route.params.id || this.id;
      if (!checklistId) return;
      if (this.metadata?._id === checklistId) return;
      this.tasklistLoadStarted = true;
      this.refreshData(true);
    });
  },
  // Vue 2 compatibility - beforeDestroy is equivalent to beforeUnmount
  beforeDestroy() {
    this.isComponentActive = false;
  },
  computed: {
    breadcrumbs() {
      return [
        {
          text: 'Home',
          testId: 'tasklist__breadcrumbs-home',
        },
        {
          text: 'Tasks',
          testId: 'tasklist__breadcrumbs-tasks',
        },
        {
          text: this.metadata?.description || '',
          noTransform: true,
          testId: 'tasklist__breadcrumbs-desc',
        },
      ];
    },
    /**
     * Min due date: cycle end − MAX_DUE_DATE_DAYS working days (weekends excluded).
     */
    dueDateMinDate() {
      return getCycleDueDateWindow(this.metadata?.to, MAX_DUE_DATE_DAYS).minIso;
    },
    /**
     * Max due date: cycle end + MAX_DUE_DATE_DAYS working days (weekends excluded).
     */
    dueDateMaxDate() {
      return getCycleDueDateWindow(this.metadata?.to, MAX_DUE_DATE_DAYS).maxIso;
    },
    /**
     * Calculates responsive table height based on viewport.
     * Uses different offsets for small vs large screens to maximize table visibility.
     */
    responsiveTableHeight() {
      // Dynamic offset based on screen height:
      let offset = TABLE_OFFSET_SMALL_SCREEN;
      if (this.windowHeight >= LARGE_SCREEN_HEIGHT_THRESHOLD) {
        offset = TABLE_OFFSET_LARGE_SCREEN;
      }
      return `calc(100vh - ${offset}px)`;
    },
    chatbotEnabled() {
      return this.$store.state.chatbotEnabled;
    },
    horizonViewEnabled() {
      return !!this.$store.getters['tasks/getFormDataV3']?.horizonViewEnabled;
    },
    isHorizonView() {
      return this.viewMode === true && this.horizonViewEnabled;
    },
    hasTasklistMetadata() {
      if (this.metadata?._id) {
        return true;
      }
      const checklistId = this.id || this.$route.params.id;
      const storeMeta = this.$store.getters['tasks/getTaskListData']?.metadata;
      return !!(checklistId && storeMeta?._id && storeMeta._id === checklistId);
    },
    horizonToolbarProps() {
      if (this.horizonToolbarBridge && Object.keys(this.horizonToolbarBridge).length) {
        return this.horizonToolbarBridge;
      }
      return {
        horizonFilters: {},
        activeFilters: {},
        startDate: '',
        endDate: '',
        cycleStart: this.metadata?.from || '',
        cycleEnd: this.metadata?.to || '',
      };
    },
    ...mapState(['tasklistUserType']),
    customColumns() {
      return applyCustomColumnSchema(this.localCustomColumns, this.customColumnSchema);
    },
    importCustomColumns() {
      const schema = this.customColumnSchema?.length
        ? this.customColumnSchema
        : (this.$store.getters['tasks/getTaskListData']?.customColumnSchema
          || this.$store.getters['tasks/getFormDataV3']?.clientCustomColumns
          || []);
      if (Array.isArray(schema) && schema.length) {
        return getCustomColumns({}, schema).filter((col) => {
          if (!col?.columnName) return false;
          const entry = schema.find((s) => s.columnName === col.columnName);
          return !entry?.isSystem;
        });
      }
      return (this.customColumns || []).filter(
        (col) => col?.columnName && !IMPORT_BLOCKED_CUSTOM_KEYS.has(col.columnName)
      );
    },
    taskListData() {
      return this.$store.getters['tasks/getTaskListData']?.checklist || [];
    },
    tasklistLoad() {
      return this.$store.getters['tasks/getTasklistLoad'];
    },
    tasklistFullyLoaded() {
      return this.$store.getters['tasks/isTasklistFullyLoaded'];
    },
    /**
     * Refresh on this page, while later task pages are still loading: an open page
     * that is not in the loaded slice goes to page 1. Back from task detail does not.
     */
    snapUnloadedTasklistPageToFirst() {
      return !this.preserveTasklistPageOnPartialLoad && !this.tasklistFullyLoaded;
    },
    /** Progress shown beside the header refresh button; null for another cycle's load. */
    tasklistHeaderLoadProgress() {
      const load = this.tasklistLoad;
      if (!this.hasTasklistMetadata || !load || String(load.cycleId || '') !== String(this.id || '')) return null;
      return {
        loaded: load.loaded,
        total: load.total,
        status: load.status,
        error: load.error || '',
        retrying: this.tasklistRetrying,
      };
    },
    transactionMatchingList() {
      return this.$store.getters['tasks/getTransactionMatchingList'];
    },
    usersList() {
      return this.$store.getters['tasks/getTaskListData']?.usersList || [];
    },
    // O(1) lookup map: userId → { _id, name, email, isActive } (from store getter)
    userMap() {
      return this.$store.getters['tasks/getUserMap'];
    },
    // O(1) lookup map: email → userId (from store getter)
    userEmailMap() {
      return this.$store.getters['tasks/getUserEmailMap'];
    },
    locationList() {
      return this.$store.getters['tasks/getTaskListData']?.locationList || [];
    },
    locationThresholds() {
      return this.$store.getters['tasks/getTaskListData']?.locationThresholds || [];
    },
    chartAcc() {
      return this.$store.getters['tasks/getTaskListData']?.chartAcc || [];
    },

    // Get role-specific summary based on active userType
    activeSummary() {
      if (this.userType === 'manager') {
        return this.$store.getters['tasks/getManagerSummary'] || {};
      } else if (this.userType === 'preparer') {
        return this.$store.getters['tasks/getPreparerSummary'] || {};
      } else if (this.userType === 'approver') {
        return this.$store.getters['tasks/getApproverSummary'] || {};
      }
      return {};
    },
    hasManagerRole() {
      const managerList = this.metadata?.managerlist;
      if (!Array.isArray(managerList) || !this.userid) return false;
      return managerList.some((id) => String(id) === String(this.userid));
    },
    availableUserTypeOptions() {
      const { ROLE_OPTIONS, NON_MANAGER_ROLE_VALUES } = this.$constants.TASK_LIST_CONSTANTS;
      return this.hasManagerRole
        ? ROLE_OPTIONS
        : ROLE_OPTIONS.filter((role) => NON_MANAGER_ROLE_VALUES.includes(role.value));
    },
    tasklistDrawerBindings() {
      return {
        legacyTaskTypeLabelChecklistName: LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
        legacyTaskTypeLabelChecklist: LEGACY_TASK_TYPE_LABEL_CHECKLIST,
        uploadData: this.uploaddata,
        checklistUploadFile: this.file,
        checklistResetTrigger: this.resetFlag,
        checklistShowDataWithError: this.showdatawitherror,
        checklistFilteredData: this.filteredData,
        checklistErrorsOnly: this.errorsonly,
        checkListErrorResponse: this.checkListErrorResponse,
        checklistHasValidationErrors: this.flag,
        bulkEditFileDataList: this.bulkEditFileDataList,
        bulkEditFile: this.bulkEditFileDataFile,
        bulkEditResetTrigger: this.resetbulkEditFile,
        bulkEditShowDataWithError: this.showdatawitherrorEditBulk,
        bulkEditTableHeaders: this.bulkEditTableHeaders,
        bulkEditFilteredData: this.filteredDataEditBluk,
        bulkEditRowErrors: this.errorsonlyEditBulk,
        bulkEditErrorResponse: this.bulkEditErrorResponse,
        bulkEditHasFileDataError: this.bulkEditFileDataerror,
        bulkEditHasValidationErrors: this.flageditBulk,
        bulkEditLoading: this.bulkEditDialogloading,
        workpaperFiles: this.fileuplaoded,
        workpaperResetTrigger: this.resetFlag,
        workpaperFileRemark: this.fileremark,
        workpaperUploadWithoutFile: this.uploadiwithoutfile,
        viewTaskRemark: this.viewtaskdata?.remark || '',
        viewTaskRemarkDate: this.viewtaskdata?.updated_at
          ? this.$nova.formatDate(this.viewtaskdata.updated_at)
          : '',
      };
    },
    filteredData() {
      if (this.showdatawitherror) {
        return this.uploaddata.filter((_, rowIndex) =>
          Object.values(this.dataerror[rowIndex]).some((error) => error)
        );
      }
      return this.uploaddata; // Return all rows if not filtering errors
    },
    filteredDataEditBluk() {
      if (this.showdatawitherrorEditBulk && this.bulkEditFileDataerror) {
        return this.bulkEditFileDataList.filter((_, rowIndex) =>
          Object.values(this.dataerroreditBulk[rowIndex]).some((error) => error)
        );
      }
      return this.bulkEditFileDataList; // Return all rows if not filtering errors
    },
    filteredColumns() {
      if (!this.search?.trim()) {
        return this.configureColumns;
      }
      const searchTerm = this.search.trim().toLowerCase();
      return this.configureColumns.filter((item) =>
        item.toLowerCase().includes(searchTerm)
      );
    },

    filteredHeaders() {
      const searchTerm = this.searchcolumn.toLowerCase();
      return this.tableHeadersfilter.filter((header) =>
        header.text.toLowerCase()?.includes(searchTerm)
      );
    },

  },
  watch: {
    'tasklistLoad.loaded'() {
      this.syncProgressiveTasklistRows();
    },
    'tasklistLoad.revision'() {
      this.syncProgressiveTasklistRows();
    },
    'tasklistLoad.status'(status) {
      this.syncProgressiveTasklistRows();
      if (status === TASKLIST_LOAD_STATUS.COMPLETE) this.syncHorizonAfterDataLoad();
    },
    viewMode(mode) {
      if (!this.viewModeToggleReady) return;
      this.onViewModeChange(mode);
    },
    '$route.query.view'(view) {
      const wantsHorizon = view === HORIZON_VIEWS.HORIZON;
      if (!wantsHorizon) {
        if (this.viewMode) this.viewMode = false;
        return;
      }
      const formDataLoaded = !!this.$store.getters['tasks/getFormDataV3'];
      if (!formDataLoaded) {
        this.viewMode = true;
        return;
      }
      const nextMode = this.horizonViewEnabled;
      if (this.viewMode !== nextMode) {
        this.viewMode = nextMode;
      }
    },
    horizonViewEnabled(enabled) {
      if (!enabled) {
        this.enforceHorizonViewAccess();
      } else if (this.$route.query.view === HORIZON_VIEWS.HORIZON && !this.viewMode) {
        this.viewMode = true;
      }
    },
    isHorizonView(isHorizon) {
      if (isHorizon && this.hasTasklistMetadata) {
        this.syncHorizonAfterDataLoad();
      }
    },
    showdatawitherror(newVal) {
      if (newVal) {
        this.errorsonly = this.dataerror.filter((item) =>
          Object.values(item).some((error) => error)
        );
      } else {
        this.errorsonly = this.dataerror;
      }
    },
    showdatawitherrorEditBulk(newVal) {
      if (newVal) {
        this.errorsonlyEditBulk = this.dataerroreditBulk.filter((item) =>
          Object.values(item).some((error) => error)
        );
      } else {
        this.errorsonlyEditBulk = this.dataerroreditBulk;
      }
    },
    hasManagerRole(isManager, wasManager) {
      if (isManager === wasManager) return;
      if (this.userType) return;
      // Manager access for this cycle just became known — apply default role once.
      this.$nextTick(() => {
        this.applyResolvedUserType(this.resolveInitialUserType());
      });
    },
  },
};
</script>
